import React, { useEffect, useState, useRef, useCallback } from 'react'
import {
  Room,
  RoomEvent,
  ConnectionState,
  ConnectionQuality,
  DisconnectReason,
  Track,
  LogLevel,
  LocalAudioTrack,
  LocalTrackPublication,
  LocalVideoTrack,
  ParticipantEvent,
  type RemoteParticipant,
} from 'livekit-client'
import type { BackgroundProcessorWrapper } from '@livekit/track-processors'
import {
  LIVEKIT_BACKGROUND_EFFECT_CHANGED_EVENT,
  LIVEKIT_CAMERA_BACKGROUND_STATE_EVENT,
  LIVEKIT_CAMERA_PERMISSION_BLOCKED_EVENT,
  LIVEKIT_SET_BACKGROUND_EFFECT_EVENT,
  VIDEO_TOGGLE_COOLDOWN_MS,
  cameraWaitingForBackground,
  destroyBackgroundProcessor,
  prewarmBackgroundAssets,
  publishCameraBackgroundState,
  publishVideoToggleBusy,
  readCameraBackgroundState,
  readVideoToggleBusy,
  type SetBackgroundEffectDetail,
  isBackgroundEffectActive,
  NO_BACKGROUND_EFFECT,
  type BackgroundEffect,
  type BackgroundFrameStats,
} from '../services/liveKitVideoBackground'
import {
  currentParseUserId,
  loadDurableBackgroundEffectOutcome,
  readCachedBackgroundEffect,
  writeCachedBackgroundEffect,
} from '../services/backgroundEffectPreference'
import {
  createDurableBackgroundEffectLoader,
  currentParseSessionKey,
} from './liveKitDurableEffectLoader'
import { RoomContext } from '@livekit/components-react'
import { phaserEvents, Event } from '../events/EventCenter'
import '@livekit/components-styles'
import {
  LIVEKIT_CONNECTION_EVENT,
  LiveKitConnectionStatus,
  LIVEKIT_TOGGLE_AUDIO_EVENT,
  LIVEKIT_TOGGLE_VIDEO_EVENT,
} from './LiveKitDebugDisplay'
import {
  LIVEKIT_TOGGLE_SCREEN_SHARE_EVENT,
  LIVEKIT_AUDIO_STATE_CHANGED,
  LIVEKIT_VIDEO_STATE_CHANGED,
  LIVEKIT_SCREEN_SHARE_STATE_CHANGED,
} from './LiveKitVideoDisplay/styles/LiveKitVideoDisplay.styles'
import LiveKitVideoDisplay from './LiveKitVideoDisplay'
import { CHARACTER_INITIALIZED } from '../managers/InitializationManager'
import LiveKitErrorBoundary from './LiveKitErrorBoundary'
import LiveKitAudioResumePrompt from './LiveKitAudioResumePrompt'
import {
  VIDEO_PRESETS,
  getVideoCaptureDefaults,
  videoPresetForBackgroundEffect,
  getDefaultLiveKitAudioDefaults,
  getRoomOptions,
  getConnectOptions,
  getScreenShareCameraReduction,
  SCREEN_SHARE_CAPTURE_OPTIONS,
  getLivekitReconnectUxConfig,
  MEDIA_INTERVALS,
} from '../config/mediaConfig'
import {
  resetLivekitReconnectUxPolicy,
  dispatchLivekitReconnectUx,
  dispatchLiveKitServerError,
  decideAutoReconnectAttempt,
  decideMaxAttemptsReached,
  decideHardDisconnectAutoRetryPending,
  decideHardDisconnectNoAutoRetry,
  decideReconnectingWatchdog,
  decidePostReconnectTrackMissing,
  decidePostReconnectBothTracksMissing,
} from '../services/livekitReconnectUxPolicy'
import { pageVisibilityHandler } from '../utils/pageVisibilityHandler'
import { setVisibilityAwareInterval } from '../utils/VisibilityAwareTimer'
import { decideAudioResumeAttempt, decideAudioPlaybackTransition } from '../utils/livekitAudioPlaybackPolicy'
import {
  LIVEKIT_AUDIO_RESUME_REQUESTED,
  readAudioResumeRequestForce,
  readAudioResumeRequestSource,
} from '../utils/livekitAudioResumeRequest'
import { SpeexNoiseProcessor as DenoiserProcessor } from '../services/LiveKitNoiseSuppressor'
import store from '../stores'
import { setLiveKitReconnectState, ConnectionStatus } from '../stores/UserStore'
import { trackEvent, stripUndefinedAnalyticsProps, livekitAnalyticsBaseProps } from '../services/analyticsService'
import { setLivekitAnalyticsSnapshot } from '../services/livekitAnalyticsSnapshot'
import { setRoomJoined } from '../stores/RoomStore'
import { addNotification } from '../stores/NotificationStore'
import { getPhaserGameSync } from '../PhaserGame'
import Bootstrap from '../scenes/Bootstrap'
import { Message } from '../../../types/Messages'
import Game from '../scenes/Game'
import Network from '../services/Network'
import {
  calculateReconnectDelayMs,
  decideAdaptiveCameraPlan,
  decideCameraEnableAction,
  getNavigatorNetworkInformation,
  getLiveKitConnectTimeoutMs,
  isBrowserOffline,
  measureServerRtt,
  resolveMediaStreamTrack,
  selectStartupMediaPlan,
  selectLiveKitConnectionProfile,
  toMediaTrackConstraints,
} from '../services/livekitNetworkResilience'
import { getServerBaseUrl } from '@/utils/serverUrl'
import { normalizeLiveKitServerUrl } from '../utils/livekitUrl'
import { getPageLeaveDisconnectDelayMs, shouldDeferPageLeaveDisconnect, type LiveKitPageLeaveReason } from '../utils/pageLifecyclePolicy'
import { shouldAutoReconnectLiveKit } from '../services/livekitReconnectDecision'
import { isCameraPermissionError, isRetryableCameraStartError } from '../utils/mediaDeviceUtils'
import { writeMediaPreference } from '../utils/mediaPreferences'
import { setLiveKitRoom, setLocalCameraWaiting } from '../services/liveKitMediaStatusStore'
import {
  createCameraWaitingSender,
  type CameraWaitingSender,
} from '../services/cameraWaitingSignal'
import { isNonRetryableScreenShareError } from './LiveKitScreenShareUtils'
import {
  decideCameraFollowUp,
  shouldTreatAsNewRoom,
  showFailedCameraStart,
} from './LiveKitRoomConnectorUtils'
import {
  createVideoBackgroundLifecycle,
  restoreDurableBackgroundEffect,
  switchToUsersBackgroundEffect,
  type ParkedCaptureProcessor,
  type VideoBackgroundLifecycle,
} from './LiveKitRoomConnectorBackground'
import {
  LIVEKIT_NATIVE_COMPUTER_SCREEN_SHARE_INTENT_EVENT,
  LIVEKIT_START_COMPUTER_SCREEN_SHARE_EVENT,
  clearActiveComputerScreenShare,
  dispatchComputerScreenShareRequest,
  releaseComputerScreenShare,
  resolveComputerScreenShareRequest,
  type ComputerScreenShareRequestDetail,
  type NativeComputerScreenShareIntent,
} from './LiveKitComputerShareUtils'

function getBootstrapNetwork(): Network | undefined {
  const g = getPhaserGameSync()
  return (g?.scene?.keys?.bootstrap as Bootstrap | undefined)?.network
}

// Add a new custom event for reconnection status
export const LIVEKIT_RECONNECTION_EVENT = 'livekit-reconnection-status'

// Add new event for manual connection control
export const LIVEKIT_MANUAL_CONNECT_EVENT = 'livekit-manual-connect'
export const LIVEKIT_MANUAL_DISCONNECT_EVENT = 'livekit-manual-disconnect'

// Add new event for auto-triggering connection after loading
export const LIVEKIT_AUTO_CONNECT_AFTER_LOADING = 'livekit-auto-connect-after-loading'
export const LIVEKIT_MANUAL_RECONNECT_EVENT = 'livekit-manual-reconnect'
export const LIVEKIT_MANUAL_DEVICE_RECONNECT_EVENT = 'livekit-manual-device-reconnect'
export const LIVEKIT_SCREEN_SHARE_SESSION_ID_EVENT = 'livekit-screen-share-session-id'

// Check if we're in development environment
const isDevelopment = process.env.NODE_ENV === 'development'

/**
 * Retries for a camera start the background lifecycle asks for once a held
 * effect is ready. The ordinary startup schedule, without its initial delay:
 * the Room is long since connected by then.
 */
const BACKGROUND_READY_CAMERA_RETRY_DELAYS_MS = [0, 1000, 2000]

// Flag to determine if user is admin - implement your own admin check logic
const isAdmin = () => {
  // First check for explicit admin flag in localStorage
  if (localStorage.getItem('isAdmin') === 'true') {
    return true
  }

  // Check for admin status in user profile data
  try {
    const userProfileData = localStorage.getItem('userProfile')
    if (userProfileData) {
      const profile = JSON.parse(userProfileData)
      return profile.isAdmin === true || profile.role === 'admin'
    }
  } catch (err) {
    // Silent fail
  }

  return false
}

// Check if we're in debug mode (development OR admin)
const isDebugMode = () => isDevelopment || isAdmin()

interface ReconnectionStatus {
  isReconnecting: boolean
  attemptCount: number
  maxAttempts: number
  nextAttemptDelay: number
}

interface LiveKitData {
  token: string
  roomName: string
  serverUrl: string
  colyseusRoomId: string
  expiresAtUnixMs?: number
}

// Connection state machine states (renamed to avoid conflict with LiveKit ConnectionState)
enum LiveKitConnectionStateMachine {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  DISCONNECTING = 'disconnecting',
  ERROR = 'error'
}

function classifyLiveKitConnectFailure(msg: string): 'timeout' | 'auth' | 'network' | 'unknown' {
  const s = msg.toLowerCase()
  if (s.includes('timeout')) return 'timeout'
  if (/jwt|unauthorized|expired|invalid.*token|401|could not establish signal connection/.test(s)) return 'auth'
  if (s.includes('network') || s.includes('fetch') || s.includes('failed to connect')) return 'network'
  return 'unknown'
}

function capAnalyticsErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err)
  const max = 480
  return raw.length <= max ? raw : `${raw.slice(0, max)}…`
}

// Valid state transitions (CONNECTED/RECONNECTING -> DISCONNECTED for remote disconnect; DISCONNECTED -> DISCONNECTED idempotent)
const VALID_TRANSITIONS: Record<LiveKitConnectionStateMachine, LiveKitConnectionStateMachine[]> = {
  [LiveKitConnectionStateMachine.DISCONNECTED]: [LiveKitConnectionStateMachine.CONNECTING, LiveKitConnectionStateMachine.DISCONNECTED, LiveKitConnectionStateMachine.CONNECTED],
  [LiveKitConnectionStateMachine.CONNECTING]: [LiveKitConnectionStateMachine.CONNECTED, LiveKitConnectionStateMachine.ERROR, LiveKitConnectionStateMachine.DISCONNECTING, LiveKitConnectionStateMachine.DISCONNECTED],
  [LiveKitConnectionStateMachine.CONNECTED]: [LiveKitConnectionStateMachine.DISCONNECTING, LiveKitConnectionStateMachine.RECONNECTING, LiveKitConnectionStateMachine.DISCONNECTED],
  [LiveKitConnectionStateMachine.RECONNECTING]: [LiveKitConnectionStateMachine.CONNECTED, LiveKitConnectionStateMachine.ERROR, LiveKitConnectionStateMachine.DISCONNECTING, LiveKitConnectionStateMachine.DISCONNECTED],
  [LiveKitConnectionStateMachine.DISCONNECTING]: [LiveKitConnectionStateMachine.DISCONNECTED],
  [LiveKitConnectionStateMachine.ERROR]: [LiveKitConnectionStateMachine.DISCONNECTED, LiveKitConnectionStateMachine.RECONNECTING, LiveKitConnectionStateMachine.CONNECTED]
}

const LiveKitRoomConnector: React.FC = () => {
  const [roomData, setRoomData] = useState<LiveKitData | null>(null)
  const [room, setRoom] = useState<Room | null>(null)
  const [roomConnected, setRoomConnected] = useState(false)
  const [audioEnabled, setAudioEnabled] = useState(false)
  const [videoEnabled, setVideoEnabled] = useState(false)
  const [screenShareEnabled, setScreenShareEnabled] = useState(false)
  const screenShareEnabledRef = useRef(screenShareEnabled)
  screenShareEnabledRef.current = screenShareEnabled
  const [preferredVideoDevice, setPreferredVideoDevice] = useState('')
  const [preferredAudioDevice, setPreferredAudioDevice] = useState('')
  const [preferredVideoEnabled, setPreferredVideoEnabled] = useState(true)
  const [preferredAudioEnabled, setPreferredAudioEnabled] = useState(true)
  const [isReconnecting, setIsReconnecting] = useState(false)

  // SC-257: remote audio can go silent when the tab is backgrounded because the
  // webAudioMix AudioContext gets suspended (Safari/iOS especially) and nothing resumes
  // it. We resume on tab return / focus / reconnect and, when the browser blocks the
  // programmatic resume, surface a click-to-resume affordance driven by
  // RoomEvent.AudioPlaybackStatusChanged.
  const [audioPlaybackBlocked, setAudioPlaybackBlocked] = useState(false)
  const audioPlaybackBlockedRef = useRef(false)
  const lastAudioResumeSourceRef = useRef<string>('auto')
  // When the last forced resume ran, so a stream of PiP-window gestures cannot force one per keystroke.
  const lastForcedAudioResumeAtRef = useRef<number | null>(null)

  // Add state for manual connection control. Default to false so we only connect AFTER loading finishes, with a clean token.
  const [manualConnectionEnabled, setManualConnectionEnabled] = useState(false)

  // Bug fix: reconnectTrigger forces the connection effect to re-run during
  // reconnection attempts. Without this, setManualConnectionEnabled(true) is a
  // no-op when it is already true (production default), so the effect's
  // dependency array never changes and the effect never re-runs.
  const [reconnectTrigger, setReconnectTrigger] = useState(0)

  // Add fallback timer for auto-connection
  const fallbackTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Refs for stable token listener (avoid re-registering on roomData/connection changes)
  const roomDataRef = useRef<LiveKitData | null>(null)
  const roomRef = useRef<Room | null>(null)
  const roomConnectedRef = useRef(false)
  const manualConnectionEnabledRef = useRef(manualConnectionEnabled)
  /** Prior token/room identity — used for reconnect + cleared on Colyseus leave */
  const previousRoomDataRef = useRef<LiveKitData | null>(null)
  roomDataRef.current = roomData
  roomRef.current = room
  roomConnectedRef.current = roomConnected
  manualConnectionEnabledRef.current = manualConnectionEnabled

  // Publish the active Room to the (non-React-context) media-status store so the
  // Phaser overlay nameplates can read live mic/camera state. See
  // `liveKitMediaStatusStore` / `useLiveKitMediaStatus`.
  useEffect(() => {
    setLiveKitRoom(room)
    return () => setLiveKitRoom(null)
  }, [room])

  // Bandwidth management: track original camera quality
  const originalCameraQualityRef = useRef<{ width?: number; height?: number; frameRate?: number } | null>(null)
  const isCameraQualityReducedRef = useRef(false)

  // Persistent screen share session ID (survives takeovers)
  const screenShareSessionIdRef = useRef<string>('')
  const activeComputerIdRef = useRef<string | null>(null)
  const screenShareOperationInFlightRef = useRef(false)

  // Connection quality tracking for adaptive quality adjustment
  const connectionQualityRef = useRef<'excellent' | 'good' | 'poor' | 'unknown'>('unknown')
  const adaptiveQualityTimerRef = useRef<NodeJS.Timeout | null>(null)
  const poorConnectionStreakRef = useRef(0)
  const networkVideoSuppressedRef = useRef(false)
  const lowBandwidthHintShownRef = useRef(false)
  const recoveryHintShownRef = useRef(false)
  const connectionProfileRef = useRef(selectLiveKitConnectionProfile(getNavigatorNetworkInformation()))
  const serverRttHintRef = useRef<number | null>(null)
  const serverRttMeasuredAtRef = useRef(0)


  // Connection state machine
  const connectionStateRef = useRef<LiveKitConnectionStateMachine>(LiveKitConnectionStateMachine.DISCONNECTED)
  const stateTransitionLockRef = useRef(false)
  /** Timestamp when the state transition lock was acquired — used for safety timeout. */
  const stateTransitionLockAcquiredAtRef = useRef<number>(0)
  // Bug 13: Track the state transition unlock timeout so it can be cleared on unmount
  const stateTransitionUnlockTimerRef = useRef<NodeJS.Timeout | null>(null)

  // RNNoise noise-suppressor processor applied on the mic track via LiveKit's Track Processor API.
  // Mute/unmute is fully handled by LiveKit's standard pub.mute()/pub.unmute() — no crackling
  // because LiveKit toggles enabled on the SOURCE getUserMedia track, not the AudioContext track.
  const denoiserProcessorRef = useRef<DenoiserProcessor | null>(null)

  // SC-158: virtual-background processor applied to the CAMERA track via the same
  // LiveKit Track Processor API used for the mic denoiser above. Screen-share is a
  // separate publication (Track.Source.ScreenShare) and is never touched.
  //
  // Built lazily on first non-'none' effect: constructing it downloads ~9 MB of
  // WASM and spins up a GPU pipeline, so users who never pick an effect pay
  // nothing. Built in the target mode and driven by switchTo() thereafter:
  // replacing a processor with setProcessor puts the raw camera on the wire.
  const videoProcessorRef = useRef<BackgroundProcessorWrapper | null>(null)
  /**
   * The effect the user wants — survives the camera being off, and every republish.
   * Seeded synchronously from localStorage so the very first camera publish already
   * carries the effect, rather than briefly showing an unprocessed frame while an
   * async config load resolves. The signed-in person's own copy (SC-452).
   * Read once per mount: a `useRef` argument is evaluated on every render, and
   * this read touches localStorage and parses JSON.
   */
  const [initialBackgroundEffect] = useState<BackgroundEffect>(readCachedBackgroundEffect)
  const backgroundEffectRef = useRef<BackgroundEffect>(initialBackgroundEffect)
  /** Rolling window of frame timings feeding the slow-device warning. */
  const backgroundFrameStatsRef = useRef<BackgroundFrameStats[]>([])
  /**
   * Set just before the background lifecycle unpublishes a camera it is holding
   * (an effect that cannot be shown), so the RC3 "camera stopped unexpectedly"
   * handler does not start it again past the camera-start gate.
   */
  const intentionalCameraUnpublishRef = useRef(false)
  /**
   * True while the segmentation pipeline is being rebuilt.
   *
   * Every camera restart makes the library destroy and re-init the processor, and
   * each init allocates a **new WebGL context**. Toggling faster than the browser
   * reclaims them pushes the page past its context ceiling, and the browser then
   * evicts the least-recently-used context — often the Phaser game canvas, which
   * is blank until the browser restores it, if it does. Gating the toggle while a
   * rebuild is in flight keeps the churn bounded.
   */
  // The busy state itself is the module's (readVideoToggleBusy): one copy, read
  // by every camera button, including one that mounts during the cooldown.
  const videoToggleCooldownTimerRef = useRef<NodeJS.Timeout | null>(null)
  /**
   * Latest-ref for applyVideoBackground, for effects and listeners that outlive
   * the render that created them.
   */
  const applyVideoBackgroundRef = useRef<
    | ((
        targetRoom: Room | null,
        effect: BackgroundEffect,
        options?: { persist?: boolean }
      ) => Promise<void>)
    | null
  >(null)
  /**
   * Latest-ref for scheduleCameraEnable: the background lifecycle is created
   * before it and asks for a camera start once a held effect is ready.
   */
  const scheduleCameraEnableRef = useRef<
    | ((
        targetRoom: Room,
        device: string | undefined,
        retryDelaysMs: number[],
        delayMs: number
      ) => void)
    | null
  >(null)
  /**
   * A processor parked in `videoCaptureDefaults` for the **next** camera start.
   *
   * This is what removes the last visible flash. LiveKit awaits `setProcessor`
   * inside `createLocalTracks`, before the track is published — so a processor
   * parked here means the very first frame that reaches the local preview and
   * the SFU is already composited. Attaching afterwards, on the publish event,
   * always left a window where the user's real room was live.
   *
   * Parking costs nothing: constructing a processor allocates no WebGL context,
   * that happens in `init()` when LiveKit attaches it.
   *
   * It is single-use. LiveKit starts it inside `createLocalTracks` and it then
   * belongs to that track — destroyed when the track is stopped (unpublished,
   * or replaced on a room switch) — so one is parked afresh after each camera
   * start rather than reused. A camera turned off and on is muted and unmuted,
   * not unpublished: its track keeps its processor and restarts it.
   */
  /**
   * The capture preset for the current effect state. [SC-158]
   *
   * Segmentation quality is bounded by capture resolution — see
   * BACKGROUND_EFFECT_VIDEO_PRESET. Every site that starts the camera goes
   * through this so the connection profile's network-driven choice and the
   * effect's detail-driven one cannot disagree between the initial publish and a
   * later toggle, which would republish at a different size.
   */
  const captureVideoPreset = useCallback(() => {
    return videoPresetForBackgroundEffect(
      connectionProfileRef.current.videoPreset,
      isBackgroundEffectActive(backgroundEffectRef.current)
    )
  }, [])

  const parkedCaptureProcessorRef = useRef<ParkedCaptureProcessor | null>(null)

  // Concurrency guard: prevents overlapping enableMicWithDenoiser calls
  const micEnableInProgressRef = useRef(false)

  // SC-377: the mic state last asked for. With the mic saved off, the first
  // unmute runs the whole enable, which can take seconds (a permission prompt,
  // the device opening, the denoiser loading). A toggle clicked meanwhile only
  // records itself here, and the enable applies the last one when it finishes.
  const micWantedOnRef = useRef(false)
  // SC-377: set when a toggle is clicked while an enable runs, which that
  // enable applies. The restore window keeps restores out of the toggle
  // analytics, so without this the click would never be recorded.
  const micUserTouchedRef = useRef(false)

  // Concurrency guard: prevents overlapping setCameraEnabled(true) calls (SC-374).
  // Uses a monotonic operation ID so that stale .finally() callbacks from older
  // in-flight promises don't clear the lock while a newer enable is in progress.
  const cameraEnableOpIdRef = useRef(0)
  const cameraEnableInProgressRef = useRef(false)

  // Skip analytics when restoring devices after reconnection (avoids duplicate APP_livekit_audio_toggled)
  const isRestoringDevicesRef = useRef(false)

  // Timeout for delayed device enable after Connected (avoid "engine not connected" publish failure)
  const deviceEnableTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const delayedCameraEnableTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Track previous device ids so we only run device-preference switch on actual change (not on first run after connect)
  const previousPreferredVideoDeviceRef = useRef<string | null>(null)
  const previousPreferredAudioDeviceRef = useRef<string | null>(null)

  const syncEffectiveVideoState = useCallback((enabled: boolean) => {
    setVideoEnabled(enabled)
    window.dispatchEvent(new CustomEvent(LIVEKIT_VIDEO_STATE_CHANGED, { detail: { enabled } }))
  }, [])

  // SC-257: best-effort resume of the webAudioMix AudioContext + any autoplay-blocked
  // audio elements. Safe to call repeatedly — no-op when disconnected, or when already
  // playing and not forced. Programmatic resume can be rejected without a user gesture (e.g.
  // Cmd/Ctrl+Tab); in that case the click-to-resume affordance is the recovery path.
  // `source` is recorded so the resume analytics event can attribute what triggered it.
  const tryResumeAudioPlayback = useCallback(async (r: Room | null, source: string, force = false) => {
    if (!r) return
    if (r.state !== ConnectionState.Connected) return
    // `force` exists because `canPlaybackAudio` cannot see the failure this routine is really for. It tracks
    // whether an audio *element* was allowed to start; the SDK never watches the AudioContext's own state, so a
    // context the browser suspended behind a background tab leaves the flag true and this gate shut. A request
    // carrying a real user gesture therefore skips it — `startAudio()` is idempotent and resumes a suspended
    // context on the way through. But a PiP window forces on every gesture, and each `startAudio()` rebuilds
    // the Web Audio graph, so on a room that reports itself playing the helper allows one per cooldown and
    // leaves the resume source alone. [SC-257 × SC-209]
    const now = Date.now()
    const decision = decideAudioResumeAttempt({
      connected: true,
      canPlaybackAudio: r.canPlaybackAudio,
      force,
      lastForcedAt: lastForcedAudioResumeAtRef.current,
      now,
    })
    if (!decision.attempt) return
    if (force) lastForcedAudioResumeAtRef.current = now
    if (decision.recordSource) lastAudioResumeSourceRef.current = source
    try {
      await r.startAudio()
      console.log(`[LiveKit] Audio playback resumed (${source})`)
    } catch (e) {
      console.warn(`[LiveKit] startAudio() blocked (${source}); awaiting user gesture`, e)
    }
  }, [])

  // SC-257 (verification aid): expose the LiveKit Room on window in dev builds so QA can
  // confirm the mechanism — `__lkRoom.canPlaybackAudio` flips to false when audio is
  // suspended in a background tab, and `__lkRoom.startAudio()` restores it. Gated
  // strictly to development builds — never expose internal Room state in production.
  useEffect(() => {
    if (!room || !isDevelopment) return
    ;(window as any).__lkRoom = room
    return () => {
      if ((window as any).__lkRoom === room) {
        delete (window as any).__lkRoom
      }
    }
  }, [room])

  // SC-257: while playback is blocked, resume on the *next* user interaction anywhere in
  // the app — any gesture satisfies the browser's autoplay requirement, so audio recovers
  // the instant the user clicks/types/taps, even before they reach the resume banner (this
  // mirrors LiveKit's own <StartAudio>). Listeners exist only while blocked and are torn
  // down as soon as playback resumes.
  useEffect(() => {
    if (!audioPlaybackBlocked) return
    const resume = () => { void tryResumeAudioPlayback(roomRef.current, 'user-interaction') }
    const opts: AddEventListenerOptions = { passive: true }
    document.addEventListener('pointerdown', resume, opts)
    document.addEventListener('keydown', resume, opts)
    document.addEventListener('touchstart', resume, opts)
    return () => {
      document.removeEventListener('pointerdown', resume, opts)
      document.removeEventListener('keydown', resume, opts)
      document.removeEventListener('touchstart', resume, opts)
    }
  }, [audioPlaybackBlocked, tryResumeAudioPlayback])

  // SC-257 × SC-209: the document listeners above only hear gestures in *this* document. A click inside a
  // Document Picture-in-Picture window is dispatched in the PiP document, and with the window open the tab
  // never becomes visible again, so neither recovery path could fire from there. The PiP layer asks instead.
  // The gesture itself does reach us — Chrome 126+ propagates user activation between a PiP window and its
  // opener — so `startAudio()` is allowed; it only has to be triggered. Registered for the connector's whole
  // life rather than only while blocked: a PiP gesture is a forced request, and it has to reach a room that
  // reports itself playing while its AudioContext is suspended. `tryResumeAudioPlayback` lets one such forced
  // attempt through per `FORCED_AUDIO_RESUME_COOLDOWN_MS`, and keeps unforced requests a no-op unless playback
  // is blocked. A request that arrives a tick before `audioPlaybackBlocked` catches up must still work too.
  useEffect(() => {
    const onAudioResumeRequested: EventListener = (event) => {
      void tryResumeAudioPlayback(
        roomRef.current,
        readAudioResumeRequestSource(event),
        readAudioResumeRequestForce(event)
      )
    }
    window.addEventListener(LIVEKIT_AUDIO_RESUME_REQUESTED, onAudioResumeRequested)
    return () => window.removeEventListener(LIVEKIT_AUDIO_RESUME_REQUESTED, onAudioResumeRequested)
  }, [tryResumeAudioPlayback])

  const clearDelayedCameraEnable = useCallback(() => {
    if (delayedCameraEnableTimeoutRef.current) {
      clearTimeout(delayedCameraEnableTimeoutRef.current)
      delayedCameraEnableTimeoutRef.current = null
    }
  }, [])

  /**
   * SC-158: mark the segmentation pipeline as rebuilding, disabling the camera
   * toggle until it settles. Cleared by the first processed frame, or by the
   * cooldown if no frame arrives (the effect failed, or the camera stayed off).
   */
  const beginVideoToggleCooldown = useCallback(() => {
    publishVideoToggleBusy(true)
    if (videoToggleCooldownTimerRef.current) clearTimeout(videoToggleCooldownTimerRef.current)
    videoToggleCooldownTimerRef.current = setTimeout(() => {
      videoToggleCooldownTimerRef.current = null
      publishVideoToggleBusy(false)
    }, VIDEO_TOGGLE_COOLDOWN_MS)
  }, [])

  const endVideoToggleCooldown = useCallback(() => {
    if (videoToggleCooldownTimerRef.current) {
      clearTimeout(videoToggleCooldownTimerRef.current)
      videoToggleCooldownTimerRef.current = null
    }
    publishVideoToggleBusy(false)
  }, [])

  /**
   * SC-158: resolve the live local CAMERA track, or null when the camera is off.
   * Deliberately narrowed to Track.Source.Camera so screen-share (a separate
   * publication) can never be processed.
   */
  const findLocalCameraTrack = useCallback((targetRoom: Room): LocalVideoTrack | null => {
    const publication = Array.from(targetRoom.localParticipant.trackPublications.values())
      .find((pub) => pub.source === Track.Source.Camera && pub.track)
    const track = publication?.track
    return track instanceof LocalVideoTrack ? track : null
  }, [])

  /**
   * The connector's side of every processed frame: clearing the busy state.
   *
   * The library's `init()` resolves once the pipeline is *wired*, not once it
   * has produced output, so a published track can exist for some frames before
   * anything is composited. The first frame is the only honest signal that the
   * effect is actually on screen. Frame timing and the slow-device warning live
   * in the background lifecycle, which wraps this.
   */
  const makeFrameProcessedHandler = useCallback(
    (_targetRoom: Room) => (_stats: BackgroundFrameStats) => {
      endVideoToggleCooldown()
    },
    [endVideoToggleCooldown]
  )

  /**
   * SC-158: parking, applying, adopting on publish and tearing down the camera
   * background effect. The rules live in `LiveKitRoomConnectorBackground` so
   * they can be tested against a fake Room; this component supplies the refs
   * and the app-facing side effects.
   *
   * Created once. Every dependency handed over only reads refs or calls
   * callbacks that are themselves stable for the life of the component.
   */
  const backgroundLifecycleRef = useRef<VideoBackgroundLifecycle | null>(null)
  if (!backgroundLifecycleRef.current) {
    backgroundLifecycleRef.current = createVideoBackgroundLifecycle({
      effectRef: backgroundEffectRef,
      processorRef: videoProcessorRef,
      parkedRef: parkedCaptureProcessorRef,
      frameStatsRef: backgroundFrameStatsRef,
      findLocalCameraTrack,
      makeFrameProcessedHandler,
      beginVideoToggleCooldown,
      announceEffect: (effect, reverted) => {
        window.dispatchEvent(new CustomEvent(LIVEKIT_BACKGROUND_EFFECT_CHANGED_EVENT, {
          detail: { effect, reverted },
        }))
      },
      notify: (notification) => {
        store.dispatch(addNotification(notification))
      },
      publishState: publishCameraBackgroundState,
      unpublishCamera: async (targetRoom, track) => {
        intentionalCameraUnpublishRef.current = true
        try {
          await targetRoom.localParticipant.unpublishTrack(track, true)
        } catch (error) {
          console.warn('[LiveKit] SC-158: could not take the held camera down:', error)
          // Unpublishing failed, but the track must not stay live raw.
          track.stop()
        } finally {
          intentionalCameraUnpublishRef.current = false
        }
      },
      // Read when the effect is turned off, so it follows the profile as it is
      // then. The same rule as `captureVideoPreset`, for the no-effect case.
      noEffectCapture: () =>
        VIDEO_PRESETS[
          videoPresetForBackgroundEffect(connectionProfileRef.current.videoPreset, false)
        ],
      requestCameraStart: (targetRoom) => {
        if (roomRef.current !== targetRoom) return
        if (targetRoom.state !== ConnectionState.Connected) return
        // The user's camera preference, not the effect, decides whether it is on.
        if (localStorage.getItem('preferVideoEnabled') === 'false') return
        if (networkVideoSuppressedRef.current) return
        if (findLocalCameraTrack(targetRoom)) return
        scheduleCameraEnableRef.current?.(
          targetRoom,
          localStorage.getItem('preferredVideoDevice') || undefined,
          BACKGROUND_READY_CAMERA_RETRY_DELAYS_MS,
          0
        )
      },
    })
  }
  const backgroundLifecycle = backgroundLifecycleRef.current
  applyVideoBackgroundRef.current = backgroundLifecycle.applyVideoBackground

  // An effect from storage, not a pick made here: the Join screen and the in-call
  // menu follow the event, and the camera gets it without it being saved back.
  const announceStoredBackgroundEffect = useCallback((effect: BackgroundEffect) => {
    window.dispatchEvent(new CustomEvent(LIVEKIT_BACKGROUND_EFFECT_CHANGED_EVENT, {
      detail: { effect, reverted: false },
    }))
  }, [])
  const applyStoredBackgroundEffect = useCallback((effect: BackgroundEffect) => {
    const currentRoom = roomRef.current
    if (currentRoom) {
      void applyVideoBackgroundRef.current?.(currentRoom, effect, { persist: false })
    }
  }, [])

  // The mount read and the sign-in reread share one Profile read (see the loader).
  const durableEffectLoaderRef = useRef<ReturnType<typeof createDurableBackgroundEffectLoader> | null>(null)
  if (!durableEffectLoaderRef.current) {
    durableEffectLoaderRef.current = createDurableBackgroundEffectLoader({
      load: loadDurableBackgroundEffectOutcome,
      sessionKey: currentParseSessionKey,
      pickCount: backgroundLifecycle.pickCount,
    })
  }

  /**
   * SC-158: let the durable cross-device preference override the local cache.
   *
   * The cached value already seeded the ref synchronously, so this only matters
   * when the user last chose an effect on a different device — and it
   * deliberately does nothing when the lookup fails, rather than resetting a
   * working effect because the network hiccuped. An answer is applied only for
   * the Parse user signed in when it lands (SC-452).
   */
  const restoreSavedBackgroundEffect = useCallback((isCancelled: () => boolean) => {
    return restoreDurableBackgroundEffect({
      effectRef: backgroundEffectRef,
      load: durableEffectLoaderRef.current!,
      isCancelled,
      pickCount: backgroundLifecycle.pickCount,
      currentUserId: currentParseUserId,
      writeCache: writeCachedBackgroundEffect,
      announce: announceStoredBackgroundEffect,
      apply: applyStoredBackgroundEffect,
    })
  }, [announceStoredBackgroundEffect, applyStoredBackgroundEffect])

  // SC-158: a problem with one user's background is not the next one's. The
  // connector outlives a logout, and so does the camera button's warning.
  useEffect(() => {
    let unmounted = false
    let lastUserId = store.getState().user.userData?.id ?? null
    const unsubscribe = store.subscribe(() => {
      const userId = store.getState().user.userData?.id ?? null
      if (userId === lastUserId) return
      lastUserId = userId
      backgroundLifecycle.forgetUser()
      // SC-452: start from this person's own device copy, never the last one's.
      // Parse already has them: every sign-in path sets it before the store.
      // A guest has a store id but no Parse user, so no copy.
      switchToUsersBackgroundEffect({
        effectRef: backgroundEffectRef,
        deviceCopy: userId ? readCachedBackgroundEffect() : NO_BACKGROUND_EFFECT,
        announce: announceStoredBackgroundEffect,
        apply: applyStoredBackgroundEffect,
      })
      // SC-452: the mount read cannot be trusted to have seen this user's saved
      // choice. Signing in here ended the account's other sessions, so a reload
      // in another browser reads on a dead session, gets nothing and keeps its
      // own cache. Read again once someone is signed in.
      if (userId) void restoreSavedBackgroundEffect(() => unmounted)
    })
    return () => {
      unmounted = true
      unsubscribe()
    }
  }, [
    backgroundLifecycle,
    restoreSavedBackgroundEffect,
    announceStoredBackgroundEffect,
    applyStoredBackgroundEffect,
  ])

  /**
   * SC-158 (F8): whether the camera the user wants on is waiting for its
   * background — readied, or kept off by a problem. The camera button and our
   * own nameplate show it, and everyone else is told over a data message,
   * because to them it is otherwise a camera turned off. Someone with no effect
   * never reaches "waiting", so nothing is ever sent for them.
   */
  const cameraWaitingSenderRef = useRef<CameraWaitingSender | null>(null)
  if (!cameraWaitingSenderRef.current) cameraWaitingSenderRef.current = createCameraWaitingSender()
  useEffect(() => {
    const sender = cameraWaitingSenderRef.current!
    const refresh = (wantsCamera?: boolean) => {
      const wants = wantsCamera ?? localStorage.getItem('preferVideoEnabled') !== 'false'
      const waiting = wants && cameraWaitingForBackground(readCameraBackgroundState())
      setLocalCameraWaiting(waiting)
      sender.update(room, waiting)
    }
    const handleStateChanged = () => refresh()
    // The toggle's own value: the listener that persists it may run after this one.
    const handleToggle = (event: CustomEvent<{ enabled?: unknown }>) => {
      const enabled = event.detail?.enabled
      refresh(typeof enabled === 'boolean' ? enabled : undefined)
    }
    const handleConnected = () => {
      if (room) sender.connected(room)
    }
    const handleParticipantConnected = (participant: RemoteParticipant) => {
      if (room) sender.participantJoined(room, participant.identity)
    }
    refresh()
    window.addEventListener(LIVEKIT_CAMERA_BACKGROUND_STATE_EVENT, handleStateChanged)
    window.addEventListener(LIVEKIT_TOGGLE_VIDEO_EVENT, handleToggle as EventListener)
    room?.on(RoomEvent.Connected, handleConnected)
    room?.on(RoomEvent.Reconnected, handleConnected)
    room?.on(RoomEvent.ParticipantConnected, handleParticipantConnected)
    return () => {
      window.removeEventListener(LIVEKIT_CAMERA_BACKGROUND_STATE_EVENT, handleStateChanged)
      window.removeEventListener(LIVEKIT_TOGGLE_VIDEO_EVENT, handleToggle as EventListener)
      room?.off(RoomEvent.Connected, handleConnected)
      room?.off(RoomEvent.Reconnected, handleConnected)
      room?.off(RoomEvent.ParticipantConnected, handleParticipantConnected)
    }
  }, [room])

  // SC-158: runs once. This read may run on a dead session, so the user-change
  // subscription above reads again at sign-in (SC-452).
  useEffect(() => {
    // Overlap the WASM/model/image fetches with room connection, so init() is
    // not doing cold network work while the camera waits for it.
    prewarmBackgroundAssets(backgroundEffectRef.current)

    let cancelled = false
    void restoreSavedBackgroundEffect(() => cancelled)
    return () => { cancelled = true }
  }, [])

  const scheduleCameraEnable = useCallback((
    targetRoom: Room,
    freshVideoDevice: string | undefined,
    retryDelaysMs: number[],
    delayMs: number
  ) => {
    clearDelayedCameraEnable()

    const maxRetries = Math.max(0, retryDelaysMs.length - 1)
    const isPublishTimeoutError = (err: unknown) => {
      const msg = (err as Error)?.message ?? String(err)
      return /engine not connected|timeout|PublishTrackError/i.test(msg)
    }

    // Bug fix (LiveKit publish race): the previous implementation silently
    // returned when room.state was momentarily 'connecting' or 'reconnecting',
    // leaving the local camera unpublished while the UI still showed it as on.
    // We now defer to a pure decision helper that retries on transient states
    // and only aborts on terminal disconnects or after exhausting attempts.
    const TRANSIENT_STATE_MAX_ATTEMPTS = 8
    const TRANSIENT_STATE_RETRY_MS = 750

    const tryCamera = (attempt: number, transientAttempt: number) => {
      const retryDelayMs = retryDelaysMs[Math.min(attempt, retryDelaysMs.length - 1)] ?? 0
      const run = () => {
        const decision = decideCameraEnableAction({
          roomMatches: !!roomRef.current && roomRef.current === targetRoom,
          roomState: targetRoom.state as string,
          attempt: transientAttempt,
          maxAttempts: TRANSIENT_STATE_MAX_ATTEMPTS,
          transientRetryDelayMs: TRANSIENT_STATE_RETRY_MS,
        })

        if (decision.action === 'abort') {
          console.warn('[LiveKit] Camera enable aborted:', decision.reason)
          syncEffectiveVideoState(false)
          return
        }

        if (decision.action === 'retry-later') {
          delayedCameraEnableTimeoutRef.current = setTimeout(
            () => tryCamera(attempt, transientAttempt + 1),
            decision.delayMs
          )
          return
        }

        if (cameraEnableInProgressRef.current) {
          console.warn('[LiveKit] scheduleCameraEnable: camera enable already in progress — skipping')
          return
        }

        const opId = ++cameraEnableOpIdRef.current
        cameraEnableInProgressRef.current = true
        // SC-158: with an effect selected the camera starts only once a ready
        // processor is parked, so its first frame out is composited. A `false`
        // keeps it off; the lifecycle has told the user why and asks again
        // once the effect is ready.
        void backgroundLifecycle.prepareCameraStart(targetRoom)
          .then((mayStart) => {
            if (!mayStart || roomRef.current !== targetRoom) return undefined
            // Turned off while the gate waited for the effect.
            if (localStorage.getItem('preferVideoEnabled') === 'false') return undefined
            const cameraOptions = getVideoCaptureDefaults(
              freshVideoDevice || undefined,
              captureVideoPreset()
            )
            return targetRoom.localParticipant
              .setCameraEnabled(true, cameraOptions)
              .then(() => true)
          })
          .then((started) => {
            if (!started) return undefined
            if (networkVideoSuppressedRef.current) return undefined
            // SC-158 (E10): the user may have turned the camera off, or off and
            // on, while this start waited for its background.
            const wantsOn = localStorage.getItem('preferVideoEnabled') !== 'false'
            const followUp = decideCameraFollowUp({
              wantsOn,
              cameraOn: targetRoom.localParticipant.isCameraEnabled,
              // This start is the one settling; the guard kept every other out.
              startRunning: false,
              videoSuppressed: false,
            })
            if (followUp === 'turn-off') return targetRoom.localParticipant.setCameraEnabled(false)
            if (followUp === 'start') {
              // On a timer, so it runs once this start has let go of the guard.
              scheduleCameraEnableRef.current?.(targetRoom, freshVideoDevice, retryDelaysMs, 0)
              return undefined
            }
            if (!wantsOn) return undefined
            syncEffectiveVideoState(true)
            // The camera is live, so access is not blocked. Connect, reconnect,
            // RC3 and the gate's own start all come through here, and none goes
            // through toggleVideo's clear: without this the buttons keep saying
            // "Camera blocked" over a live camera.
            window.dispatchEvent(
              new CustomEvent(LIVEKIT_CAMERA_PERMISSION_BLOCKED_EVENT, { detail: { blocked: false } })
            )
            dispatchConnectionStatus(targetRoom.state as ConnectionState, targetRoom)
            return undefined
          })
          .catch((err) => {
            // SC-158 (E10): failed for a camera the user has turned off since:
            // nothing to retry, and no "couldn't start" notice or warning icon.
            if (localStorage.getItem('preferVideoEnabled') === 'false') {
              backgroundLifecycle.forgetCameraStart(targetRoom)
              return
            }
            if (isPublishTimeoutError(err) && attempt < maxRetries) {
              tryCamera(attempt + 1, 0)
            } else {
              console.error('Failed to enable camera:', err)
              showFailedCameraStart(
                () => backgroundLifecycle.handleCameraStartFailed(targetRoom),
                () => syncEffectiveVideoState(false)
              )
            }
          })
          .finally(() => {
            if (cameraEnableOpIdRef.current === opId) {
              cameraEnableInProgressRef.current = false
            }
          })
      }

      if (retryDelayMs === 0) run()
      else delayedCameraEnableTimeoutRef.current = setTimeout(run, retryDelayMs)
    }

    delayedCameraEnableTimeoutRef.current = setTimeout(() => {
      delayedCameraEnableTimeoutRef.current = null
      tryCamera(0, 0)
    }, delayMs)
  }, [clearDelayedCameraEnable, syncEffectiveVideoState, backgroundLifecycle])
  scheduleCameraEnableRef.current = scheduleCameraEnable

  const logCalculatedConnectionProfile = useCallback((
    source: 'initial-effect' | 'network-change' | 'pre-connect',
    networkInfo: ReturnType<typeof getNavigatorNetworkInformation> | null,
    measuredRtt: number | null,
    profile: ReturnType<typeof selectLiveKitConnectionProfile>
  ) => {
    console.log('[LiveKit] Calculated connection profile', {
      source,
      effectiveType: networkInfo?.effectiveType ?? null,
      saveData: networkInfo?.saveData ?? null,
      downlink: networkInfo?.downlink ?? null,
      navigatorRtt: networkInfo?.rtt ?? null,
      measuredRtt,
      profileKey: profile.key,
      videoPreset: profile.videoPreset,
      startWithVideoDisabled: profile.startWithVideoDisabled,
      connectOptionsOverrides: profile.connectOptionsOverrides,
    })
  }, [])

  const logAdaptiveCameraPlan = useCallback((
    quality: 'excellent' | 'good' | 'poor' | 'unknown',
    plan: ReturnType<typeof decideAdaptiveCameraPlan>,
    cameraEnabled: boolean
  ) => {
    console.log('[LiveKit] Calculated adaptive camera plan', {
      quality,
      poorOrUnknownStreak: plan.poorOrUnknownStreak,
      constraints: plan.constraints,
      shouldDisableVideo: plan.shouldDisableVideo,
      cameraEnabled,
      networkVideoSuppressed: networkVideoSuppressedRef.current,
      screenShareEnabled,
    })
  }, [screenShareEnabled])

  // Helper function to transition connection state safely
  const transitionConnectionState = useCallback((newState: LiveKitConnectionStateMachine, reason?: string) => {
    const currentState = connectionStateRef.current

    // Check if transition is valid
    const validTransitions = VALID_TRANSITIONS[currentState]
    if (!validTransitions.includes(newState)) {
      console.warn(`[LiveKit] Invalid state transition from ${currentState} to ${newState}`, { reason })
      return false
    }

    // Prevent concurrent transitions
    if (stateTransitionLockRef.current) {
      const lockAge = Date.now() - stateTransitionLockAcquiredAtRef.current
      const LOCK_SAFETY_TIMEOUT_MS = 30_000

      if (lockAge > LOCK_SAFETY_TIMEOUT_MS) {
        console.warn(`[LiveKit] State transition lock held for ${lockAge}ms — force-releasing`, {
          currentState,
          attemptedState: newState,
        })
        stateTransitionLockRef.current = false
        // Fall through to allow the transition
      } else if (
        newState === LiveKitConnectionStateMachine.CONNECTING ||
        newState === LiveKitConnectionStateMachine.RECONNECTING ||
        newState === LiveKitConnectionStateMachine.DISCONNECTING
      ) {
        console.warn(`[LiveKit] State transition blocked - transition already in progress`, { currentState, attemptedState: newState })
        return false
      }
    }

    // Lock transitions during critical operations
    if (newState === LiveKitConnectionStateMachine.CONNECTING || newState === LiveKitConnectionStateMachine.RECONNECTING || newState === LiveKitConnectionStateMachine.DISCONNECTING) {
      stateTransitionLockRef.current = true
      stateTransitionLockAcquiredAtRef.current = Date.now()
    }

    connectionStateRef.current = newState

    if (isDebugMode()) {
      console.log(`[LiveKit] State transition: ${currentState} -> ${newState}`, { reason })
    }

    // Bug 13 fix: Track the unlock timeout so it can be cleared on unmount
    // to prevent stale unlock after the component is destroyed
    if (newState === LiveKitConnectionStateMachine.CONNECTED || newState === LiveKitConnectionStateMachine.DISCONNECTED || newState === LiveKitConnectionStateMachine.ERROR) {
      if (stateTransitionUnlockTimerRef.current) {
        clearTimeout(stateTransitionUnlockTimerRef.current)
      }
      stateTransitionUnlockTimerRef.current = setTimeout(() => {
        stateTransitionLockRef.current = false
        stateTransitionUnlockTimerRef.current = null
      }, 100)
    }

    return true
  }, [])

  /**
   * Force DISCONNECTED at once, dropping the transition lock and its pending
   * unlock. Not transitionConnectionState: that holds the lock for 100 ms, and
   * a connect run started right after (an effect re-run, the next Room) would
   * bail on it with nothing left to start it again.
   */
  const forceDisconnectedState = useCallback(() => {
    connectionStateRef.current = LiveKitConnectionStateMachine.DISCONNECTED
    stateTransitionLockRef.current = false
    if (stateTransitionUnlockTimerRef.current) {
      clearTimeout(stateTransitionUnlockTimerRef.current)
      stateTransitionUnlockTimerRef.current = null
    }
  }, [])

  // When Colyseus room leaves, hard-reset LiveKit + token state so media cannot outlive the game room
  useEffect(() => {
    const handleRoomLeft = () => {
      const currentRoom = roomRef.current
      if (currentRoom) {
        console.log('[LiveKit] Colyseus room left — disconnecting LiveKit and clearing token state')
        try {
          currentRoom.disconnect()
        } catch (e) {
          console.warn('[LiveKit] disconnect() during room left:', e)
        }
      }
      setRoom(null)
      setRoomConnected(false)
      setRoomData(null)
      previousRoomDataRef.current = null
      forceDisconnectedState()
    }
    phaserEvents.on(Event.ROOM_LEFT, handleRoomLeft)
    return () => {
      phaserEvents.off(Event.ROOM_LEFT, handleRoomLeft)
    }
  }, [forceDisconnectedState])

  // Helper to check if we can perform an operation based on current state
  const canPerformOperation = useCallback((operation: 'connect' | 'disconnect' | 'reconnect'): boolean => {
    const currentState = connectionStateRef.current

    switch (operation) {
      case 'connect':
        return currentState === LiveKitConnectionStateMachine.DISCONNECTED || currentState === LiveKitConnectionStateMachine.ERROR
      case 'disconnect':
        return currentState === LiveKitConnectionStateMachine.CONNECTED || currentState === LiveKitConnectionStateMachine.CONNECTING || currentState === LiveKitConnectionStateMachine.RECONNECTING
      case 'reconnect':
        return currentState === LiveKitConnectionStateMachine.CONNECTED || currentState === LiveKitConnectionStateMachine.ERROR || currentState === LiveKitConnectionStateMachine.DISCONNECTED
      default:
        return false
    }
  }, [])

  /**
   * Enable mic via LiveKit's standard API and apply the RNNoise denoiser processor.
   *
   * After this, mute/unmute is crackle-free because:
   *  - `setTrackMuted` is patched to use the processor's GainNode fade
   *    instead of toggling `mediaStreamTrack.enabled` (which causes
   *    buffer-discontinuity crackling on AudioContext destination tracks).
   *  - The muted/unmuted events are still emitted so the server and UI
   *    see the correct state.
   */
  const enableMicWithDenoiser = useCallback(
    // `userInitiated`: a click, tracked even inside the restore window that
    // keeps connect and reconnect restores out of the toggle analytics.
    async (r: Room, deviceId?: string, { userInitiated = false } = {}) => {
      // The call bar saves a mute before it asks for one, so a mic saved off
      // here was muted after this was scheduled: a restore after a delay or a
      // retry, a device switch, the unpublished-mic recovery. Opening it would
      // undo the click, and a timed-out publish never completes later, so a
      // retry is the only thing that could. A click that turns the mic on has
      // saved it on first.
      if (!userInitiated && localStorage.getItem('preferAudioEnabled') === 'false') return
      if (micEnableInProgressRef.current) {
        console.warn('[LiveKit] enableMicWithDenoiser already in progress — skipping')
        return
      }
      micEnableInProgressRef.current = true
      // Every enable that gets this far is for a mic that is on: a restore of
      // one saved off returned above. Only a mute clicked from here on says
      // otherwise.
      micWantedOnRef.current = true
      micUserTouchedRef.current = false
      try {
        // Destroy previous processor if any
        if (denoiserProcessorRef.current) {
          try { await denoiserProcessorRef.current.destroy() } catch { /* ignore */ }
          denoiserProcessorRef.current = null
        }

        const microphoneOptions = {
          ...getDefaultLiveKitAudioDefaults(deviceId),
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: true,
        }

        // Use LiveKit's standard API to enable the mic.
        // Browser noise suppression is OFF because RNNoise handles it.
        // AEC and AGC are ON because they work independently at the
        // WebRTC layer and don't conflict with our AudioContext pipeline.
        const pub = await r.localParticipant.setMicrophoneEnabled(true, microphoneOptions)
        if (!pub?.track) throw new Error('No mic track after setMicrophoneEnabled')

        let processorFallback = false

        try {
          // Apply RNNoise denoiser processor on the track
          const processor = new DenoiserProcessor()
          const storedVolume = localStorage.getItem('micInputVolume')
          if (storedVolume) {
            processor.setVolume(parseFloat(storedVolume))
          }
          await (pub.track as LocalAudioTrack).setProcessor(processor)
          denoiserProcessorRef.current = processor

          // Patch setTrackMuted so mute/unmute fades the GainNode
          // instead of toggling enabled — eliminates crackling.
          processor.patchTrackMute(pub.track as unknown as Parameters<DenoiserProcessor['patchTrackMute']>[0])
          processorFallback = (processor as any).fallback === true
        } catch (processorError) {
          processorFallback = true
          denoiserProcessorRef.current = null
          console.warn('[LiveKit] Noise suppressor unavailable, continuing with raw microphone track:', processorError)
        }

        // Toggles clicked while this ran (SC-377), applied only now, through the
        // patched mute: LiveKit's own mute disables the source track, which the
        // patched unmute never re-enables, so a mute before patchTrackMute would
        // leave the next unmute silent. Repeated until a toggle clicked during
        // the mute or unmute itself has been applied too.
        let wantedOn = micWantedOnRef.current
        for (;;) {
          if (!wantedOn && !pub.isMuted) await pub.mute()
          else if (wantedOn && pub.isMuted) await pub.unmute()
          if (micWantedOnRef.current === wantedOn) break
          wantedOn = micWantedOnRef.current
        }

        setAudioEnabled(wantedOn)
        if (userInitiated || micUserTouchedRef.current || !isRestoringDevicesRef.current) {
          trackEvent(
            'APP_livekit_audio_toggled',
            stripUndefinedAnalyticsProps({
              enabled: wantedOn,
              ...livekitAnalyticsBaseProps(roomDataRef.current?.colyseusRoomId, roomDataRef.current?.roomName),
            }),
            'Livekit'
          )
        }
        // Notify server of mic state for spontaneous interaction tracking
        getBootstrapNetwork()?.room?.send(
          wantedOn ? Message.AUDIO_CONNECTED : Message.AUDIO_DISCONNECTED
        )
        window.dispatchEvent(
          new CustomEvent('livekit-device-state', {
            detail: {
              cameraEnabled: r.localParticipant.isCameraEnabled,
              microphoneEnabled: wantedOn,
            },
          })
        )
        if (!wantedOn) {
          // The call bar may have flipped back on when the publication appeared.
          window.dispatchEvent(
            new CustomEvent(LIVEKIT_AUDIO_STATE_CHANGED, { detail: { enabled: false } })
          )
        }
        dispatchConnectionStatus(r.state as ConnectionState, r)

        // Notify user if noise cancellation fell back to passthrough
        if (processorFallback) {
          console.warn('[LiveKit] RNNoise denoiser fell back to passthrough — noise cancellation is inactive')
          store.dispatch(addNotification({
            message: 'Noise cancellation unavailable — background noise may be audible',
            type: 'warning',
            duration: 8000,
          }))
        }
      } catch (err) {
        console.error('Failed to enable microphone with denoiser processor:', err)
        throw err
      } finally {
        micEnableInProgressRef.current = false
      }
    },
    []
  )

  // Standard LiveKit helpers — no special handling needed.
  // With the Track Processor, pub.mute()/pub.unmute() toggles `enabled`
  // on the SOURCE getUserMedia track, which Chrome handles cleanly.
  const getLocalMicPublication = useCallback((r: Room): LocalTrackPublication | undefined => {
    return r.localParticipant.getTrackPublication(Track.Source.Microphone) as LocalTrackPublication | undefined
  }, [])
  const muteLocalMicrophone = useCallback(
    async (r: Room): Promise<void> => {
      const pub = getLocalMicPublication(r)
      if (pub && typeof (pub as LocalTrackPublication).mute === 'function') {
        await (pub as LocalTrackPublication).mute()
      }
    },
    [getLocalMicPublication]
  )
  const unmuteLocalMicrophone = useCallback(
    async (r: Room): Promise<void> => {
      const pub = getLocalMicPublication(r)
      if (pub && typeof (pub as LocalTrackPublication).unmute === 'function') {
        await (pub as LocalTrackPublication).unmute()
      }
    },
    [getLocalMicPublication]
  )

  // Load preferred devices from localStorage (missing = enabled, same as PermissionsDialog)
  useEffect(() => {
    try {
      const videoDeviceId = localStorage.getItem('preferredVideoDevice')
      const audioDeviceId = localStorage.getItem('preferredAudioDevice')
      const videoEnabled = localStorage.getItem('preferVideoEnabled') !== 'false'
      const audioEnabled = localStorage.getItem('preferAudioEnabled') !== 'false'
      if (videoDeviceId) setPreferredVideoDevice(videoDeviceId)
      if (audioDeviceId) setPreferredAudioDevice(audioDeviceId)
      setPreferredVideoEnabled(videoEnabled)
      setPreferredAudioEnabled(audioEnabled)
    } catch (e) {
      console.error('Error loading device preferences:', e)
    }
  }, [])

  useEffect(() => {
    const updateConnectionProfile = async () => {
      const source = getNavigatorNetworkInformation() ? 'network-change' : 'initial-effect'
      const networkInfo = getNavigatorNetworkInformation()
      let measuredRtt = serverRttHintRef.current

      if ((!networkInfo || networkInfo.rtt == null) && typeof window !== 'undefined') {
        const probe = await measureServerRtt(getServerBaseUrl())
        if (typeof probe === 'number') {
          measuredRtt = probe
          serverRttHintRef.current = probe
          serverRttMeasuredAtRef.current = Date.now()
        }
      }

      connectionProfileRef.current = selectLiveKitConnectionProfile(
        measuredRtt != null
          ? { ...(networkInfo ?? {}), rtt: measuredRtt }
          : networkInfo
      )

      logCalculatedConnectionProfile(source, networkInfo, measuredRtt, connectionProfileRef.current)
    }

    void updateConnectionProfile()

    const networkInfo = getNavigatorNetworkInformation() as (EventTarget & {
      addEventListener?: (type: string, listener: EventListenerOrEventListenerObject) => void
      removeEventListener?: (type: string, listener: EventListenerOrEventListenerObject) => void
    }) | null

    const onNetworkChange = () => { void updateConnectionProfile() }

    networkInfo?.addEventListener?.('change', onNetworkChange)

    return () => {
      networkInfo?.removeEventListener?.('change', onNetworkChange)
    }
  }, [logCalculatedConnectionProfile])

  // Listen for token event (stable listener: registered once so token is never missed)
  useEffect(() => {
    const handleLiveKitToken = (data: LiveKitData) => {
      const normalizedServerUrl = normalizeLiveKitServerUrl(data.serverUrl)
      console.log('[LiveKit] Token listener: handleLiveKitToken called, setting roomData', { roomName: data.roomName, hasToken: !!data.token, hasServerUrl: !!normalizedServerUrl })
      setRoomData((prev) => {
        if (
          prev?.token === data.token &&
          prev?.roomName === data.roomName &&
          prev?.colyseusRoomId === data.colyseusRoomId &&
          prev?.serverUrl === normalizedServerUrl
        ) {
          console.log('[LiveKit] Token identical to current state, skipping update')
          return prev
        }
        return {
          ...data,
          serverUrl: normalizedServerUrl,
          ...(typeof data.expiresAtUnixMs === 'number' && Number.isFinite(data.expiresAtUnixMs)
            ? { expiresAtUnixMs: data.expiresAtUnixMs }
            : {}),
        }
      })

      // Set up fallback timer for auto-connection after 20 seconds (use refs for latest state)
      // This ensures we eventually connect even if the LIVEKIT_AUTO_CONNECT_AFTER_LOADING event gets lost
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current)
      }
      fallbackTimerRef.current = setTimeout(() => {
        if (!roomConnectedRef.current && roomDataRef.current && !manualConnectionEnabledRef.current) {
          console.log('🔄 [Fallback] Auto-connecting to LiveKit after timeout')
          setManualConnectionEnabled(true)
        }
      }, 20000) // 20 second fallback

      // In debug mode, log the token receipt but don't auto-connect
      if (isDebugMode()) {
        console.log('🔧 [Debug Mode] LiveKit token received but auto-connection disabled')
        console.log('   Use the debug panel to manually connect to save bandwidth')
      }
    }
    phaserEvents.on(Event.LIVEKIT_TOKEN_RECEIVED, handleLiveKitToken)
    return () => {
      phaserEvents.off(Event.LIVEKIT_TOKEN_RECEIVED, handleLiveKitToken)
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current)
      }
    }
  }, [])

  // Listen for LIVEKIT_ERROR_RECEIVED - tiered UX; avoid immediate token retry on rate limits (prevents feedback loop with server).
  useEffect(() => {
    const handleLiveKitError = (data: { message: string; retryable?: boolean }) => {
      console.error('[LiveKit] Error received:', data.message)
      const msg = (data.message || '').toLowerCase()
      const rateLimited = msg.includes('too many token requests')
      dispatchLiveKitServerError(data.message || 'LiveKit setup failed', data.retryable !== false)
      setManualConnectionEnabled(true)
      if (data.retryable === false || rateLimited) {
        return
      }
      const phaserGame = getPhaserGameSync()
      const bootstrap = phaserGame?.scene?.keys?.bootstrap as Bootstrap | undefined
      if (bootstrap?.network) {
        bootstrap.network.requestLiveKitToken({ discretionary: true })
      }
    }
    phaserEvents.on(Event.LIVEKIT_ERROR_RECEIVED, handleLiveKitError)
    return () => { phaserEvents.off(Event.LIVEKIT_ERROR_RECEIVED, handleLiveKitError) }
  }, [])

  // Listen for LIVEKIT_RECONNECT_REQUESTED (from nameplate click) - check cooldown, request token
  useEffect(() => {
    const handleReconnectRequested = () => {
      const state = store.getState().user.liveKitReconnectState
      if (state.cooldownUntil != null && state.cooldownUntil > Date.now()) return // In cooldown, ignore
      store.dispatch(setLiveKitReconnectState({ showPrompt: true, message: 'Reconnecting...' }))
      const phaserGame = getPhaserGameSync()
      const bootstrap = phaserGame?.scene?.keys?.bootstrap as Bootstrap | undefined
      if (bootstrap?.network) {
        bootstrap.network.requestLiveKitToken({ force: true })
      }
    }
    phaserEvents.on(Event.LIVEKIT_RECONNECT_REQUESTED, handleReconnectRequested)
    return () => { phaserEvents.off(Event.LIVEKIT_RECONNECT_REQUESTED, handleReconnectRequested) }
  }, [])

  /** After Colyseus token reconnect: refresh LiveKit JWT only when stale, or reconcile transport if LiveKit disconnected (avoids duplicate token requests vs Network). */
  useEffect(() => {
    const onColyseusReconnected = () => {
      const lkRoom = roomRef.current
      const net = getBootstrapNetwork()
      if (lkRoom?.state === ConnectionState.Connected) {
        const tok = roomDataRef.current?.token
        if (net && tok && net.isLiveKitJwtDueRefresh(tok)) {
          void net.requestLiveKitTokenAndWait(20000)
        }
        return
      }
      void (async () => {
        try {
          await net?.requestLiveKitTokenAndWait(20000, { discretionary: true })
        } catch (e) {
          console.warn('[LiveKit] Token handshake after Colyseus reconnect failed:', e)
        }
        window.dispatchEvent(new CustomEvent(LIVEKIT_MANUAL_RECONNECT_EVENT))
      })()
    }
    phaserEvents.on(Event.COLYSEUS_RECONNECTED, onColyseusReconnected)
    return () => {
      phaserEvents.off(Event.COLYSEUS_RECONNECTED, onColyseusReconnected)
    }
  }, [])

  /** Optional: notification / UI can ask to retry media publish without full page reload */
  useEffect(() => {
    const onRetryMediaPublish = () => {
      window.dispatchEvent(new CustomEvent(LIVEKIT_MANUAL_RECONNECT_EVENT))
      window.dispatchEvent(new CustomEvent(LIVEKIT_MANUAL_DEVICE_RECONNECT_EVENT))
    }
    window.addEventListener('livekit-retry-media-publish', onRetryMediaPublish)
    return () => window.removeEventListener('livekit-retry-media-publish', onRetryMediaPublish)
  }, [])

  // Listen for device preference changes from DeviceSelectMenu
  useEffect(() => {
    const handleDevicePreferenceChanged = (event: any) => {
      const customEvent = event as CustomEvent<{ kind: 'videoinput' | 'audioinput', deviceId: string }>;
      const { kind, deviceId } = customEvent.detail;
      
      if (kind === 'videoinput') {
        setPreferredVideoDevice(deviceId);
      } else if (kind === 'audioinput') {
        setPreferredAudioDevice(deviceId);
      }
    };

    const handleMicVolumeChanged = (event: any) => {
      const customEvent = event as CustomEvent<{ volume: number }>;
      const { volume } = customEvent.detail;
      if (denoiserProcessorRef.current) {
        denoiserProcessorRef.current.setVolume(volume);
      }
    };

    window.addEventListener('livekit-device-preference-changed', handleDevicePreferenceChanged);
    window.addEventListener('livekit-mic-volume-changed', handleMicVolumeChanged);
    return () => {
      window.removeEventListener('livekit-device-preference-changed', handleDevicePreferenceChanged);
      window.removeEventListener('livekit-mic-volume-changed', handleMicVolumeChanged);
    };
  }, []);

  // Listen for manual connection events from debug display
  useEffect(() => {
    const handleManualConnect = () => {
      console.log('🔧 [Debug Mode] Manual connection requested')
      setManualConnectionEnabled(true)
    }

    const handleManualDisconnect = () => {
      console.log('🔧 [Debug Mode] Manual disconnection requested')
      setManualConnectionEnabled(false)
      // Disconnect current room if connected
      if (room) {
        room.disconnect()
        setRoom(null)
        setRoomConnected(false)
      }
    }

    // Auto-connect after loading is complete
    const handleAutoConnectAfterLoading = () => {
      console.log('🚀 [LiveKit] Auto-connecting to LiveKit after loading completion')

      void (async () => {
        try {
          const net = typeof getBootstrapNetwork === 'function' ? getBootstrapNetwork() : null
          const cur = roomDataRef.current
          if (net && cur?.token) {
            console.log('[LiveKit] Ensuring token is fresh before final connection...')
            await net.requestLiveKitTokenAndWait(5000, { discretionary: true })
          }
        } catch (e) {
          console.warn('[LiveKit] Pre-connect token check failed:', e)
        }

        console.log('   Current state:', {
          hasRoomData: !!roomDataRef.current,
          hasToken: !!roomDataRef.current?.token,
          manualConnectionEnabled: manualConnectionEnabledRef.current,
          roomConnected: roomConnectedRef.current
        })
        setManualConnectionEnabled(true)
      })()

      // Clear fallback timer since we're connecting now
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current)
        fallbackTimerRef.current = null
      }
    }

    // Manual reconnect event
    const handleManualReconnect = () => {
      console.log('🔄 Manual reconnection requested')
      if (room) {
        room.disconnect()
        setRoom(null)
        setRoomConnected(false)
      }
      // Re-enable connection to trigger reconnection
      setManualConnectionEnabled(true)
    }

    const handleBrowserOnline = () => {
      const currentRoomState = roomRef.current?.state
      const currentData = roomDataRef.current

      if (!currentData?.token || !currentData.serverUrl) {
        return
      }

      if (currentRoomState === ConnectionState.Connected || currentRoomState === ConnectionState.Reconnecting) {
        return
      }

      if (reconnectTimeoutRef.current) {
        return
      }

      console.log('[LiveKit] Browser came back online, retrying room connection')
      setManualConnectionEnabled(true)
      setReconnectTrigger(prev => prev + 1)
    }

    window.addEventListener(LIVEKIT_MANUAL_CONNECT_EVENT, handleManualConnect)
    window.addEventListener(LIVEKIT_MANUAL_DISCONNECT_EVENT, handleManualDisconnect)
    window.addEventListener(LIVEKIT_AUTO_CONNECT_AFTER_LOADING, handleAutoConnectAfterLoading)
    window.addEventListener(LIVEKIT_MANUAL_RECONNECT_EVENT, handleManualReconnect)
    window.addEventListener('online', handleBrowserOnline)

    return () => {
      window.removeEventListener(LIVEKIT_MANUAL_CONNECT_EVENT, handleManualConnect)
      window.removeEventListener(LIVEKIT_MANUAL_DISCONNECT_EVENT, handleManualDisconnect)
      window.removeEventListener(LIVEKIT_AUTO_CONNECT_AFTER_LOADING, handleAutoConnectAfterLoading)
      window.removeEventListener(LIVEKIT_MANUAL_RECONNECT_EVENT, handleManualReconnect)
      window.removeEventListener('online', handleBrowserOnline)
    }
  }, [room])

  // Track previous room data to detect actual room changes
  const connectionLockRef = useRef(false)
  /** Room created by the in-flight connection effect (for cleanup if deps change mid-async). */
  const pendingConnectRoomRef = useRef<Room | null>(null)

  // Connection recovery with exponential backoff (thresholds from getLivekitReconnectUxConfig)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const delayedDisconnectRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectingWatchdogRef = useRef<NodeJS.Timeout | null>(null)
  /** Bumps when Reconnected fires so stale post-reconnect verify timeouts are ignored */
  const postReconnectVerifyGenRef = useRef(0)

  const clearReconnectingWatchdog = useCallback(() => {
    if (reconnectingWatchdogRef.current) {
      clearTimeout(reconnectingWatchdogRef.current)
      reconnectingWatchdogRef.current = null
    }
  }, [])

  const clearDelayedDisconnectRetry = useCallback(() => {
    if (delayedDisconnectRetryTimeoutRef.current) {
      clearTimeout(delayedDisconnectRetryTimeoutRef.current)
      delayedDisconnectRetryTimeoutRef.current = null
    }
  }, [])

  // Exponential backoff reconnection
  const attemptReconnection = useCallback(() => {
    if (roomRef.current?.state === ConnectionState.Connected || roomRef.current?.state === ConnectionState.Reconnecting) {
      console.log('[LiveKit] Skipping scheduled reconnect because room is already recovering')
      return
    }

    const lkUx = getLivekitReconnectUxConfig()
    const maxReconnectAttempts = lkUx.maxAutoReconnectAttempts

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.error('[LiveKit] Max reconnection attempts reached, giving up')
      transitionConnectionState(LiveKitConnectionStateMachine.ERROR, 'Max reconnection attempts reached')
      dispatchLivekitReconnectUx(
        decideMaxAttemptsReached(lkUx, reconnectAttemptsRef.current, maxReconnectAttempts),
        { forceModal: true }
      )
      return
    }

    if (!roomData || !roomData.token || !roomData.serverUrl) {
      console.warn('[LiveKit] Cannot reconnect: missing room data')
      return
    }

    if (reconnectTimeoutRef.current) {
      console.log('[LiveKit] Reconnection already scheduled, skipping duplicate request')
      return
    }

    if (isBrowserOffline(typeof navigator !== 'undefined' ? navigator : undefined)) {
      store.dispatch(setLiveKitReconnectState({
        showPrompt: true,
        showReconnectionDialog: false,
        message: 'You are offline. Waiting for network to reconnect...',
      }))
      console.warn('[LiveKit] Browser is offline, deferring LiveKit reconnect scheduling')
      return
    }

    const delay = calculateReconnectDelayMs({
      attempt: reconnectAttemptsRef.current,
      baseDelayMs: lkUx.baseReconnectDelayMs,
    })
    reconnectAttemptsRef.current++

    dispatchLivekitReconnectUx(decideAutoReconnectAttempt(lkUx, reconnectAttemptsRef.current, maxReconnectAttempts))

    console.log(`[LiveKit] Attempting reconnection ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${delay}ms`)

    reconnectTimeoutRef.current = setTimeout(async () => {
      reconnectTimeoutRef.current = null

      if (isBrowserOffline(typeof navigator !== 'undefined' ? navigator : undefined)) {
        console.warn('[LiveKit] Browser went offline before reconnect attempt; waiting for network recovery')
        return
      }

      try {
        const net = getBootstrapNetwork()
        const cur = roomDataRef.current
        if (net && cur?.token && net.isLiveKitJwtDueRefresh(cur.token)) {
          await net.requestLiveKitTokenAndWait(20000)
        }
      } catch (e) {
        console.warn('[LiveKit] Pre-reconnect token refresh failed:', e)
      }
      // Transition to DISCONNECTED so the connection effect can run connect logic again
      // (canPerformOperation('connect') is true only for DISCONNECTED or ERROR)
      transitionConnectionState(LiveKitConnectionStateMachine.DISCONNECTED, `Reconnection attempt ${reconnectAttemptsRef.current}`)
      setManualConnectionEnabled(true)
      // Incrementing reconnectTrigger guarantees a state change that forces the
      // connection effect to re-run, even when manualConnectionEnabled is already true.
      setReconnectTrigger(prev => prev + 1)
    }, delay)
  }, [roomData, canPerformOperation, transitionConnectionState])

  const attemptReconnectionRef = useRef(attemptReconnection)
  attemptReconnectionRef.current = attemptReconnection
  const canPerformOperationRef = useRef(canPerformOperation)
  canPerformOperationRef.current = canPerformOperation

  // Reset reconnection attempts on successful connection
  const resetReconnectionAttempts = useCallback(() => {
    reconnectAttemptsRef.current = 0
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  // Manage Room instance and connection - ONLY when roomData or connection flag changes
  useEffect(() => {
    console.log('[LiveKit] Connection effect run', { hasRoomData: !!roomData, hasToken: !!roomData?.token, hasServerUrl: !!roomData?.serverUrl, manualConnectionEnabled, state: connectionStateRef.current })

    // Don't connect if we don't have room data or if manual connection is disabled
    // Also block if user intentionally disconnected (page reload / menu return)
    if (!roomData || !roomData.token || !roomData.serverUrl || !manualConnectionEnabled) {
      console.log('[LiveKit] Connection effect bailing: missing roomData/manualConnectionEnabled')
      const r = roomRef.current
      if (r && canPerformOperation('disconnect')) {
        // Only disconnect if connection is explicitly disabled or room data is missing
        if (!manualConnectionEnabled || !roomData) {
          transitionConnectionState(LiveKitConnectionStateMachine.DISCONNECTING, 'Connection disabled or room data missing')
          backgroundLifecycle.retireRoom(r)
          r.disconnect()
          setRoom(null)
          transitionConnectionState(LiveKitConnectionStateMachine.DISCONNECTED, 'Disconnected')
        }
      }
      return
    }

    // Check if this is a new room (different token or room name)
    const isNewRoom = shouldTreatAsNewRoom(previousRoomDataRef.current, roomData)

    // If we already have a room connected to the same room, don't reconnect
    if (roomRef.current && roomConnectedRef.current && !isNewRoom) {
      if (isDebugMode()) {
        console.log('🔧 [LiveKit] Already connected to this room, skipping reconnection')
      }
      previousRoomDataRef.current = {
        ...previousRoomDataRef.current,
        token: roomData.token,
        expiresAtUnixMs: roomData.expiresAtUnixMs,
      }
      return
    }

    // New JWT (e.g. proactive pre-expiry refresh) while CONNECTED: `connect` is disallowed by
    // the state machine, so we must disconnect first or media silently desyncs mid-call.
    if (roomRef.current && roomConnectedRef.current && isNewRoom) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[LiveKit] Token or room identity changed while connected — reconnecting with new JWT')
      }
      const prev = roomRef.current
      try {
        if (canPerformOperation('disconnect')) {
          transitionConnectionState(
            LiveKitConnectionStateMachine.DISCONNECTING,
            'In-call JWT / room identity rotation'
          )
        }
        // SC-158: the next Room gets its own capture defaults; a processor
        // parked in this one's would never be used there.
        backgroundLifecycle.retireRoom(prev)
        prev.disconnect()
      } catch (e) {
        console.warn('[LiveKit] Error disconnecting for in-call token rotation:', e)
      }
      setRoom(null)
      setRoomConnected(false)
      forceDisconnectedState()
      setReconnectTrigger((t) => t + 1)
      return
    }

    // Check if we can connect based on state machine
    if (!canPerformOperation('connect')) {
      console.log('[LiveKit] Connection effect bailing: cannot connect from state', connectionStateRef.current)
      return
    }

    // Prevent concurrent connection attempts
    if (connectionLockRef.current || stateTransitionLockRef.current) {
      console.log('[LiveKit] Connection effect bailing: connection already in progress (lock or state transition)')
      return
    }

    let didCancel = false
    // True while this run holds CONNECTING but its Room is not yet in
    // pendingConnectRoomRef: the RTT probe and the background park. A cancel in
    // that window leaves the cleanup no Room to disconnect, so the cleanup has
    // to undo CONNECTING itself, or every re-run bails at
    // canPerformOperation('connect') and the call never connects.
    let connectingWithoutPendingRoom = false
    connectionLockRef.current = true
    pendingConnectRoomRef.current = null

    const run = async () => {
      let token = roomData.token
      let serverUrl = normalizeLiveKitServerUrl(roomData.serverUrl)
      let roomName = roomData.roomName
      let colyseusRoomId = roomData.colyseusRoomId
      let expiresAtUnixMs = roomData.expiresAtUnixMs

      try {
        const net = getBootstrapNetwork()
        if (net?.isLiveKitJwtDueRefresh(token)) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[LiveKit] JWT near expiry or unreadable — fetching fresh token before connect')
          }
          await net.requestLiveKitTokenAndWait(20000).catch(() => null)
          // Regardless of success, bail out.
          // If successful, LIVEKIT_TOKEN_RECEIVED handles state update and restarts effect.
          // If failed, we prevent entering a rapid retry loop inside this effect.
          // Cancelled: the cleanup already released the lock, and a leave drops
          // this wait's waiter, so it settles only on its timeout, by which time
          // a rejoin may hold the lock.
          if (!didCancel) connectionLockRef.current = false
          return
        }

        if (didCancel) {
          connectionLockRef.current = false
          return
        }

        if (!transitionConnectionState(LiveKitConnectionStateMachine.CONNECTING, 'Starting connection')) {
          connectionLockRef.current = false
          return
        }
        connectingWithoutPendingRoom = true

        console.log('🔗 [LiveKit] Connection conditions met:', {
          hasRoomData: !!roomData,
          hasToken: !!token,
          hasServerUrl: !!serverUrl,
          manualConnectionEnabled,
          isNewRoom,
          isDebugMode: isDebugMode()
        })

        console.log(isDebugMode() ? '🔧 [Debug Mode] Connecting to LiveKit room...' : 'Connecting to LiveKit room...')

        // Clean up existing room if it's a different room
        const prevRoom = roomRef.current
        if (prevRoom && isNewRoom) {
          try {
            transitionConnectionState(LiveKitConnectionStateMachine.DISCONNECTING, 'Switching to new room')
            backgroundLifecycle.retireRoom(prevRoom)
            prevRoom.disconnect()
            transitionConnectionState(LiveKitConnectionStateMachine.DISCONNECTED, 'Previous room disconnected')
          } catch (error) {
            console.warn('[LiveKit] Error disconnecting previous room:', error)
            transitionConnectionState(LiveKitConnectionStateMachine.ERROR, 'Error disconnecting previous room')
          }
          setRoom(null)
          setRoomConnected(false)
        }

        let connectionProfile = connectionProfileRef.current
        const browserNetworkInfo = getNavigatorNetworkInformation()
        const shouldRefreshRttProbe =
          (!browserNetworkInfo || browserNetworkInfo.rtt == null) &&
          (serverRttMeasuredAtRef.current === 0 || Date.now() - serverRttMeasuredAtRef.current > 60_000)

        if (shouldRefreshRttProbe) {
          const measuredRtt = await measureServerRtt(getServerBaseUrl())
          // Left during the probe: the cleanup has already released the lock
          // and reset the state, and a rejoin may hold both by now.
          if (didCancel) return
          if (typeof measuredRtt === 'number') {
            serverRttHintRef.current = measuredRtt
            serverRttMeasuredAtRef.current = Date.now()
          }
        }

        if ((!browserNetworkInfo || browserNetworkInfo.rtt == null) && serverRttHintRef.current != null) {
          connectionProfile = selectLiveKitConnectionProfile({
            ...(browserNetworkInfo ?? {}),
            rtt: serverRttHintRef.current,
          })
          connectionProfileRef.current = connectionProfile
        }

        logCalculatedConnectionProfile(
          'pre-connect',
          browserNetworkInfo,
          serverRttHintRef.current,
          connectionProfile
        )

        const savedVideoPreference = localStorage.getItem('preferVideoEnabled') !== 'false'
        const startupMediaPlan = selectStartupMediaPlan({
          connectionProfile,
          measuredRttMs: serverRttHintRef.current,
          userPrefersVideo: savedVideoPreference,
        })
        if (startupMediaPlan.keepVideoSuppressedUntilManual && savedVideoPreference) {
          networkVideoSuppressedRef.current = true
        }

        const lkVideoDevice = localStorage.getItem('preferredVideoDevice') || preferredVideoDevice || undefined
        const lkAudioDevice = localStorage.getItem('preferredAudioDevice') || preferredAudioDevice || undefined
        const videoDefaults = getVideoCaptureDefaults(
          lkVideoDevice,
          videoPresetForBackgroundEffect(
            connectionProfile.videoPreset,
            isBackgroundEffectActive(backgroundEffectRef.current)
          )
        )
        const audioDefaults = getDefaultLiveKitAudioDefaults(lkAudioDevice)
        const connectOptions = getConnectOptions(connectionProfile.connectOptionsOverrides)
        const newRoom = new Room({
          ...getRoomOptions(),
          videoCaptureDefaults: videoDefaults,
          audioCaptureDefaults: audioDefaults,
        })

        // SC-158: park the background processor before anything can start the
        // camera. LiveKit attaches it inside createLocalTracks, so the first
        // frame published — and the first frame the user sees — is already
        // composited. Awaited on purpose: attaching after publish is what left a
        // moment of the user's real room visible when joining from the lobby.
        //
        // Bounded and never fatal. An image effect needs an authenticated fetch
        // (up to its own 8 s timeout), and the join must not wait on it. Past
        // the bound the park carries on, and the camera-start gate holds the
        // camera off until it lands — nothing is published without it.
        try {
          await backgroundLifecycle.parkBeforeConnect(newRoom, backgroundEffectRef.current)
        } catch (error) {
          console.warn('[LiveKit] SC-158: could not pre-attach the background effect:', error)
        }
        // Left during the park. The cleanup found no pending Room and has
        // already released the lock and reset the state, and a rejoin may hold
        // the lock and its own pending Room by now: this Room is ours alone to
        // drop, and the shared refs are not ours to write.
        if (didCancel) {
          backgroundLifecycle.retireRoom(newRoom)
          try {
            newRoom.disconnect().catch(() => {
              /* ignore */
            })
          } catch {
            /* ignore */
          }
          return
        }
        pendingConnectRoomRef.current = newRoom
        connectingWithoutPendingRoom = false
        setRoom(newRoom)

        // Nothing between the check above and here can yield, so this cannot
        // see a cancel. Kept as a plain return: if it ever did, the cleanup
        // would have found this Room pending and dropped it, and the lock and
        // pending ref may already be a rejoin's.
        if (didCancel) return

        // Removed redundant newRoom.prepareConnection() to prevent livekit-client internal unhandled rejections during websocket failures

        const CONNECTION_TIMEOUT_MS = getLiveKitConnectTimeoutMs(connectOptions)
        let connectLostRaceToTimeout = false
        const connectPromise = newRoom.connect(serverUrl, token, connectOptions)
        connectPromise.catch(() => {
          // If the timeout wins Promise.race, connectPromise still rejects later — that must not
          // surface as an unhandled rejection (Safari / strict runtimes).
          if (connectLostRaceToTimeout) {
            return
          }
        })
        const timeoutPromise = new Promise<void>((_, reject) => {
          const timer = setTimeout(() => {
            connectLostRaceToTimeout = true
            try {
              newRoom.disconnect().catch(() => {
                /* ignore */
              })
            } catch {
              /* ignore */
            }
            reject(new Error('LiveKit connection timeout after 30s'))
          }, CONNECTION_TIMEOUT_MS)
          // Not .finally(): the promise it returns rejects with the connect's
          // error, and nothing holds it, so every failed connect was reported
          // as an unhandled rejection.
          const clear = () => clearTimeout(timer)
          connectPromise.then(clear, clear)
        })

        await Promise.race([connectPromise, timeoutPromise])

        if (didCancel) return

        connectionLockRef.current = false
        pendingConnectRoomRef.current = null
        const transitioned = transitionConnectionState(LiveKitConnectionStateMachine.CONNECTED, 'Connection established')
        if (!transitioned) {
          console.warn('[LiveKit] Forcing state to CONNECTED (fallback)')
          connectionStateRef.current = LiveKitConnectionStateMachine.CONNECTED
          stateTransitionLockRef.current = false
        }
        setRoomConnected(true)
        const effectiveData: LiveKitData = {
          token,
          serverUrl,
          roomName,
          colyseusRoomId,
          ...(typeof expiresAtUnixMs === 'number' && Number.isFinite(expiresAtUnixMs) ? { expiresAtUnixMs } : {}),
        }
        previousRoomDataRef.current = effectiveData
        resetReconnectionAttempts()
        console.log('[LiveKit] connect() succeeded')
      } catch (err: any) {
        if (didCancel) return
        connectingWithoutPendingRoom = false
        connectionLockRef.current = false
        pendingConnectRoomRef.current = null
        trackEvent(
          'APP_livekit_connection_failed',
          stripUndefinedAnalyticsProps({
            ...livekitAnalyticsBaseProps(roomData.colyseusRoomId, roomData.roomName),
            error: capAnalyticsErrorMessage(err),
            error_class: classifyLiveKitConnectFailure(capAnalyticsErrorMessage(err)),
          }),
          'Livekit'
        )
        transitionConnectionState(LiveKitConnectionStateMachine.ERROR, `Connection failed: ${err?.message ?? err}`)
        setRoomConnected(false)
        console.error('[LiveKit] connect() failed:', err)

        const msg = String(err?.message ?? err)
        const lkUxCatch = getLivekitReconnectUxConfig()
        const maxReconnectAttemptsCatch = lkUxCatch.maxAutoReconnectAttempts
        const authLike = /jwt|unauthorized|expired|invalid.*token|401|could not establish signal connection/i.test(msg)
        if (authLike && reconnectAttemptsRef.current < maxReconnectAttemptsCatch) {
          try {
            await getBootstrapNetwork()?.requestLiveKitTokenAndWait(15000)
          } catch {
            /* ignore */
          }
          // Left during the wait: a reconnect now would rejoin a call the user left.
          if (didCancel) return
        }

        if (reconnectAttemptsRef.current < maxReconnectAttemptsCatch) {
          attemptReconnection()
        } else {
          window.dispatchEvent(
            new CustomEvent('livekit-failsafe-error', {
              detail: { message: msg || 'Failed to connect to LiveKit room' },
            })
          )
        }
      }
    }

    void run().catch((err) => {
      if (didCancel) return
      console.error('[LiveKit] Connection effect run() failed (unhandled guard):', err)
      connectionLockRef.current = false
      pendingConnectRoomRef.current = null
      transitionConnectionState(
        LiveKitConnectionStateMachine.ERROR,
        `Connection failed: ${err instanceof Error ? err.message : String(err)}`
      )
      setRoomConnected(false)
    })

    return () => {
      didCancel = true
      connectionLockRef.current = false
      const newRoom = pendingConnectRoomRef.current
      pendingConnectRoomRef.current = null

      // Undoing this run's CONNECTING is forceDisconnectedState: the re-run
      // follows this cleanup at once. No newer run can hold the state yet (React
      // runs this cleanup first), and this run's own late return writes nothing
      // shared.

      if (newRoom) {
        // A cancelled connect's Room is never used again, new room or not: the
        // re-run builds its own, with the token it was re-run for. Kept, it
        // would connect with no run left to finish it, and the state would stay
        // CONNECTING.
        //
        // SC-158: nothing may stay parked in it either, including a park still
        // waiting on its image. Only here, where it is disconnected: a Room that
        // carries on connecting becomes the call's Room, and a retired one
        // refuses every park, so its camera could never start with the effect.
        backgroundLifecycle.retireRoom(newRoom)
        // Disconnected whatever the state says (SC-377). The state can already
        // read DISCONNECTED while this Room connects, e.g. after the room-switch
        // block above; skipped then, the Room finished connecting as a ghost
        // participant nobody would ever leave.
        try {
          const localParticipant = newRoom.localParticipant
          if (localParticipant) {
            const screenShareTracks = Array.from(
              localParticipant.trackPublications.values()
            ).filter(
              (pub) =>
                pub.source === Track.Source.ScreenShare ||
                pub.source === Track.Source.ScreenShareAudio
            )

            screenShareTracks.forEach((publication) => {
              try {
                if (publication.track) {
                  publication.track.stop()
                }
              } catch (error) {
                console.warn('[LiveKit] Error stopping track during cleanup:', error)
              }
            })
          }
        } catch (error) {
          console.warn('[LiveKit] Error during track cleanup:', error)
        }

        try {
          newRoom.disconnect().catch(() => {
            /* ignore */
          })
        } catch {
          /* ignore */
        }
        // Whatever the state reads, it is this run's while its Room is pending:
        // CONNECTING, or the DISCONNECTED the room switch set, which also holds
        // the 100 ms transition lock. Left held, the re-run bails on it and
        // nothing runs the effect again when it clears.
        forceDisconnectedState()
        // roomRef only catches up with setRoom(null) on the next render, and
        // until then the re-run would take this Room for the previous call's,
        // and its Disconnected handler, still attached, would act for it.
        if (roomRef.current === newRoom) roomRef.current = null
        setRoomConnected(false)
        setRoom(null)
      }

      // Cancelled during the RTT probe or the park, with no Room to drop.
      if (connectingWithoutPendingRoom && !newRoom) {
        connectingWithoutPendingRoom = false
        forceDisconnectedState()
      }
    }
  }, [
    roomData,
    manualConnectionEnabled,
    reconnectTrigger,
    canPerformOperation,
    transitionConnectionState,
    forceDisconnectedState,
  ])

  // Separate effect to update device preferences without reconnecting (only when user changes device, not on first run after connect)
  useEffect(() => {
    if (!room || !roomConnected) {
      previousPreferredVideoDeviceRef.current = null
      previousPreferredAudioDeviceRef.current = null
      return
    }

    // First run after connect: initial enable is handled by handleConnected; just record current devices and skip
    if (previousPreferredVideoDeviceRef.current === null) {
      previousPreferredVideoDeviceRef.current = preferredVideoDevice
      previousPreferredAudioDeviceRef.current = preferredAudioDevice
      return
    }

    const videoChanged = preferredVideoDevice !== previousPreferredVideoDeviceRef.current
    const audioChanged = preferredAudioDevice !== previousPreferredAudioDeviceRef.current

    if (videoChanged) {
      if (preferredVideoDevice) {
        if (room.localParticipant.isCameraEnabled) {
          // SC-158: a device switch restarts the camera, and a camera being
          // held for its first effect has no processor yet — restarted now, it
          // would go out raw. Wait for the effect to land or the camera to be
          // taken down.
          void backgroundLifecycle.whenSettled()
            .then(() => room.switchActiveDevice('videoinput', preferredVideoDevice))
            .catch((err) => {
              console.warn('[LiveKit] Failed to switch video device:', err)
            })
        }
      }
      previousPreferredVideoDeviceRef.current = preferredVideoDevice
    }

    if (audioChanged) {
      if (preferredAudioDevice && audioEnabled) {
        room.switchActiveDevice('audioinput', preferredAudioDevice).then(() => {
          // Re-apply processor just in case
          enableMicWithDenoiser(room, preferredAudioDevice).catch((err) => {
            console.warn('[LiveKit] Failed to re-apply processor for audio device:', err)
          })
        }).catch((err) => {
          console.warn('[LiveKit] Failed to switch audio device:', err)
        })
      }
      previousPreferredAudioDeviceRef.current = preferredAudioDevice
    }
  }, [room, roomConnected, preferredVideoDevice, preferredAudioDevice, audioEnabled, enableMicWithDenoiser])

  // Cleanup fallback timer when component unmounts or when connected
  useEffect(() => {
    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current)
        fallbackTimerRef.current = null
      }
    }
  }, [])

  // Clear fallback timer when successfully connected
  useEffect(() => {
    if (roomConnected && fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current)
      fallbackTimerRef.current = null
    }
  }, [roomConnected])

  // Bandwidth management: prioritize audio over video during low bandwidth
  useEffect(() => {
    if (!room || !roomConnected) return

    const checkBandwidthAndAdjust = () => {
      const connectionQuality = room.localParticipant?.connectionQuality

      // During poor connection, prioritize audio
      if (connectionQuality === 'poor' || connectionQuality === 'unknown') {
        // Ensure audio is prioritized (already handled by LiveKit, but we can verify)
        const audioTracks = Array.from(room.localParticipant.trackPublications.values())
          .filter((pub) => pub.kind === 'audio')

        const videoTracks = Array.from(room.localParticipant.trackPublications.values())
          .filter((pub) => pub.kind === 'video')

        // In poor conditions, we may want to reduce video quality further
        // This is handled by the connection quality monitoring, but we log it here
        if (isDebugMode() && videoTracks.length > 0) {
          console.log('[LiveKit] Bandwidth management: Prioritizing audio over video due to poor connection')
        }
      }
    }

    const cleanupBandwidth = setVisibilityAwareInterval(
      checkBandwidthAndAdjust,
      MEDIA_INTERVALS.bandwidthCheckMs,
      MEDIA_INTERVALS.bandwidthCheckMs * 2
    )

    return () => cleanupBandwidth()
  }, [room, roomConnected])

  // Enhanced connection quality monitoring and adaptive quality adjustment
  useEffect(() => {
    if (!room || !roomConnected) return

    // Function to adjust video quality based on connection quality
    const adjustQualityBasedOnConnection = (quality: ConnectionQuality | 'unknown') => {
      const qualityStr = typeof quality === 'string' ? quality : (ConnectionQuality[quality as ConnectionQuality]?.toLowerCase() || 'unknown')
      const userPrefersVideo = localStorage.getItem('preferVideoEnabled') !== 'false'
      const plan = decideAdaptiveCameraPlan({
        quality: qualityStr as 'excellent' | 'good' | 'poor' | 'unknown',
        poorOrUnknownStreak: poorConnectionStreakRef.current,
        userPrefersVideo,
        isVideoCurrentlyEnabled: room.localParticipant.isCameraEnabled,
        networkVideoSuppressed: networkVideoSuppressedRef.current,
        isScreenShareEnabled: screenShareEnabled,
      })

      poorConnectionStreakRef.current = plan.poorOrUnknownStreak
      connectionQualityRef.current = qualityStr
      logAdaptiveCameraPlan(
        qualityStr as 'excellent' | 'good' | 'poor' | 'unknown',
        plan,
        room.localParticipant.isCameraEnabled
      )

      try {
        const cameraTrack = Array.from(room.localParticipant.trackPublications.values())
          .find((pub) => pub.source === Track.Source.Camera && pub.track)
        const mediaStreamTrack = resolveMediaStreamTrack(cameraTrack?.track as {
          mediaStreamTrack?: MediaStreamTrack
          getSettings?: MediaStreamTrack['getSettings']
          applyConstraints?: MediaStreamTrack['applyConstraints']
        } | undefined)
        const settings = mediaStreamTrack?.getSettings?.() || {}

        // Store original quality on first adjustment
        if (!originalCameraQualityRef.current && quality !== 'excellent') {
          originalCameraQualityRef.current = {
            width: settings.width,
            height: settings.height,
            frameRate: settings.frameRate
          }
        }

        if (
          mediaStreamTrack &&
          mediaStreamTrack.readyState === 'live' &&
          (settings.width !== plan.constraints.width ||
            settings.height !== plan.constraints.height ||
            settings.frameRate !== plan.constraints.frameRate)
        ) {
          mediaStreamTrack.applyConstraints(toMediaTrackConstraints(plan.constraints)).catch((err: Error) => {
            console.warn('[LiveKit] Failed to adjust camera quality:', err)
          })

          if (isDebugMode()) {
            console.log(`[LiveKit] Adjusted quality for ${qualityStr} connection:`, plan.constraints)
          }
        }

        if ((qualityStr === 'good' || qualityStr === 'excellent') && networkVideoSuppressedRef.current && !recoveryHintShownRef.current) {
          recoveryHintShownRef.current = true
          store.dispatch(addNotification({
            type: 'info',
            message: 'Connection recovered. You can turn your camera back on if you want video again.',
            duration: 6000,
          }))
        }

        if (qualityStr === 'poor' || qualityStr === 'unknown') {
          recoveryHintShownRef.current = false
        }

        if (plan.shouldDisableVideo) {
          networkVideoSuppressedRef.current = true
          recoveryHintShownRef.current = false
          clearDelayedCameraEnable()
          room.localParticipant.setCameraEnabled(false).then(() => {
            setVideoEnabled(false)
            window.dispatchEvent(
              new CustomEvent(LIVEKIT_VIDEO_STATE_CHANGED, { detail: { enabled: false } })
            )
            window.dispatchEvent(
              new CustomEvent('livekit-device-state', {
                detail: {
                  cameraEnabled: false,
                  microphoneEnabled: room.localParticipant.isMicrophoneEnabled,
                },
              })
            )
            dispatchConnectionStatus(room.state as ConnectionState, room)
            store.dispatch(addNotification({
              type: 'warning',
              message: 'Connection is too unstable for video. Camera paused to keep audio stable.',
              duration: 9000,
            }))
          }).catch((err) => {
            console.warn('[LiveKit] Failed to pause camera during poor connection:', err)
          })
        }
      } catch (error) {
        console.warn('[LiveKit] Error adjusting quality:', error)
      }
    }

    // Handle connection quality changes via event
    const handleConnectionQualityChanged = (
      quality: ConnectionQuality,
      participant: any
    ) => {
      if (participant.isLocal) {
        adjustQualityBasedOnConnection(quality)
      }
    }

    // Set up event listener for connection quality changes
    room.on(RoomEvent.ConnectionQualityChanged, handleConnectionQualityChanged)

    // Also check periodically for quality changes (fallback)
    const qualityCheckCleanup = setVisibilityAwareInterval(
      () => {
        const connectionQuality = room.localParticipant?.connectionQuality
        if (connectionQuality) {
          adjustQualityBasedOnConnection(connectionQuality)
        }
      },
      MEDIA_INTERVALS.qualityCheckMs,
      MEDIA_INTERVALS.qualityCheckMs * 2
    )

    return () => {
      room.off(RoomEvent.ConnectionQualityChanged, handleConnectionQualityChanged)
      qualityCheckCleanup()
      if (adaptiveQualityTimerRef.current) {
        clearTimeout(adaptiveQualityTimerRef.current)
        adaptiveQualityTimerRef.current = null
      }
    }
  }, [room, roomConnected, screenShareEnabled])

  // Room event listeners
  useEffect(() => {
    if (!room) return
    const handleConnected = () => {
      clearReconnectingWatchdog()
      setRoomConnected(true)
      poorConnectionStreakRef.current = 0
      resetLivekitReconnectUxPolicy()
      // SC-257: sync audio-playback state on (re)connect. Clears a stale "blocked" flag
      // carried over from a previous session, and — if the user joined while the tab was
      // backgrounded — shows the resume affordance and attempts a programmatic resume.
      const audioBlockedOnConnect = !room.canPlaybackAudio
      audioPlaybackBlockedRef.current = audioBlockedOnConnect
      setAudioPlaybackBlocked(audioBlockedOnConnect)
      if (audioBlockedOnConnect) void tryResumeAudioPlayback(room, 'connected')
      store.dispatch(
        setLiveKitReconnectState({
          showPrompt: false,
          message: '',
          showReconnectionDialog: false,
          cooldownUntil: undefined,
          reconnectAttempt: undefined,
          maxAttempts: undefined,
        })
      )
      const roomId = roomDataRef.current?.colyseusRoomId
      const livekitRoomName = roomDataRef.current?.roomName
      trackEvent(
        'APP_livekit_connected',
        stripUndefinedAnalyticsProps({
          ...livekitAnalyticsBaseProps(roomId, livekitRoomName),
        }),
        'Livekit'
      )
      dispatchConnectionStatus(ConnectionState.Connected, room)
      setIsReconnecting(false)

      // Reset connection quality tracking
      const quality = room.localParticipant?.connectionQuality
      connectionQualityRef.current = quality ? (ConnectionQuality[quality as ConnectionQuality]?.toLowerCase() || 'unknown') : 'unknown'

      const savedVideoEnabled = localStorage.getItem('preferVideoEnabled') !== 'false'
      const savedAudioEnabled = localStorage.getItem('preferAudioEnabled') !== 'false'
      const startupMediaPlan = selectStartupMediaPlan({
        connectionProfile: connectionProfileRef.current,
        measuredRttMs: serverRttHintRef.current,
        userPrefersVideo: savedVideoEnabled,
      })
      const shouldEnableVideo =
        savedVideoEnabled &&
        !networkVideoSuppressedRef.current &&
        !startupMediaPlan.keepVideoSuppressedUntilManual

      if (connectionProfileRef.current.key !== 'default' && !lowBandwidthHintShownRef.current) {
        lowBandwidthHintShownRef.current = true
        store.dispatch(addNotification({
          type: 'info',
          message:
            connectionProfileRef.current.key === 'audio-priority'
              ? 'Very weak connection detected. Starting audio-first to keep the call stable.'
              : 'Weak connection detected. Starting with reduced video quality.',
          duration: 7000,
        }))
      }

      // Bug D fix: Re-read device IDs from localStorage rather than relying on
      // stale closure/state values. The PermissionsDialog writes these after
      // LiveKitRoomConnector has already mounted and captured the old values.
      const freshVideoDevice = localStorage.getItem('preferredVideoDevice') || ''
      const freshAudioDevice = localStorage.getItem('preferredAudioDevice') || ''

      // Also update component state so subsequent effects use the latest values
      if (freshVideoDevice) setPreferredVideoDevice(freshVideoDevice)
      if (freshAudioDevice) setPreferredAudioDevice(freshAudioDevice)

      // Sync in-game UI immediately so overlay shows intended state
      window.dispatchEvent(
        new CustomEvent(LIVEKIT_AUDIO_STATE_CHANGED, { detail: { enabled: savedAudioEnabled } })
      )
      window.dispatchEvent(
        new CustomEvent(LIVEKIT_VIDEO_STATE_CHANGED, { detail: { enabled: shouldEnableVideo } })
      )

      // Delay device enable so RTCEngine is ready (avoids "engine not connected within timeout")
      if (deviceEnableTimeoutRef.current) {
        clearTimeout(deviceEnableTimeoutRef.current)
        deviceEnableTimeoutRef.current = null
      }
      const DELAY_MS = 500 // Keep 500ms for initial connect
      deviceEnableTimeoutRef.current = setTimeout(() => {
        deviceEnableTimeoutRef.current = null
        isRestoringDevicesRef.current = true
        const r = room
        if (!r?.localParticipant) {
          isRestoringDevicesRef.current = false
          return
        }
        // Bug 14 fix: Check if room is still connected before enabling devices
        // The timeout may fire after the room has been disconnected
        if (r.state !== 'connected') {
          console.warn('[LiveKit] Skipping device enable - room is no longer connected:', r.state)
          isRestoringDevicesRef.current = false
          return
        }
        const RETRY_DELAYS_MS = startupMediaPlan.publishRetryDelaysMs
        const maxRetries = Math.max(0, RETRY_DELAYS_MS.length - 1)
        const isPublishTimeoutError = (err: unknown) => {
          const msg = (err as Error)?.message ?? String(err)
          return /engine not connected|timeout|PublishTrackError/i.test(msg)
        }

        if (!shouldEnableVideo) {
          clearDelayedCameraEnable()
          syncEffectiveVideoState(false)
          r.localParticipant.setCameraEnabled(false).catch((err) => {
            console.error('Failed to disable camera:', err)
          })
        }

        // SC-377: a mic saved off stays closed. Publishing it only to mute it
        // lit the mic light, prompted for a mic never granted, and showed the
        // user unmuted to everyone for a moment. The first unmute in game
        // publishes it (toggleAudio, with no publication yet).
        const tryMic = (attempt: number) => {
          const delayMs = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]
          const run = () => {
            // Bug D fix: Use freshAudioDevice from localStorage instead of stale closure value
            enableMicWithDenoiser(r, freshAudioDevice || undefined)
              .catch((err) => {
                if (isPublishTimeoutError(err) && attempt < maxRetries) {
                  tryMic(attempt + 1)
                } else {
                  console.error('Failed to enable microphone:', err)
                }
              })
          }
          if (delayMs === 0) run()
          else setTimeout(run, delayMs)
        }
        if (savedAudioEnabled) tryMic(0)

        if (shouldEnableVideo) {
          scheduleCameraEnable(
            r,
            freshVideoDevice || undefined,
            RETRY_DELAYS_MS,
            startupMediaPlan.cameraEnableDelayMs
          )
        }

        // Clear restore flag after async device setup completes (mirrors handleReconnected)
        setTimeout(() => {
          isRestoringDevicesRef.current = false
        }, 10000)
      }, DELAY_MS)
    }
    const handleDisconnected = (reason?: DisconnectReason) => {
      // A Room the connector already dropped (a cancelled connect) emits this
      // once its disconnect settles, before React detaches this listener. A
      // re-run may hold CONNECTING by then, and this would reset it.
      if (roomRef.current !== room) return
      clearReconnectingWatchdog()
      clearDelayedDisconnectRetry()
      poorConnectionStreakRef.current = 0
      if (deviceEnableTimeoutRef.current) {
        clearTimeout(deviceEnableTimeoutRef.current)
        deviceEnableTimeoutRef.current = null
      }
      clearDelayedCameraEnable()
      ++cameraEnableOpIdRef.current
      cameraEnableInProgressRef.current = false
      previousPreferredVideoDeviceRef.current = null
      previousPreferredAudioDeviceRef.current = null
      const roomId = roomDataRef.current?.colyseusRoomId
      trackEvent(
        'APP_livekit_disconnected',
        stripUndefinedAnalyticsProps({
          ...livekitAnalyticsBaseProps(roomId, roomDataRef.current?.roomName),
          reason: String(reason ?? 'unknown'),
        }),
        'Livekit'
      )
      // Update state machine
      transitionConnectionState(LiveKitConnectionStateMachine.DISCONNECTED, `Disconnected: ${String(reason ?? 'unknown')}`)
      setRoomConnected(false)

      const isServerKick =
        reason === DisconnectReason.SERVER_SHUTDOWN ||
        reason === DisconnectReason.PARTICIPANT_REMOVED
        // We removed ROOM_DELETED from isServerKick so that clients automatically
        // reconnect if the server forcefully deletes and recreates the room to raise capacity.

      // SC-273 AC4: decide auto-retry via a pure policy. The old inline `!!reason` guard
      // treated UNKNOWN_REASON (enum 0) and undefined as non-retryable, so abrupt transport
      // drops (ping timeout / signal close / peerconnection failed) dead-ended with no
      // recovery while the Colyseus avatar stayed. Now those heal while still room-joined.
      const colyseusJoined = store.getState().room.roomJoined
      const willAutoRetry = shouldAutoReconnectLiveKit({ reason, colyseusJoined })

      if (willAutoRetry) {
        const retryDelayMs = getLivekitReconnectUxConfig().disconnectBeforeAutoRetryDelayMs
        delayedDisconnectRetryTimeoutRef.current = setTimeout(() => {
          delayedDisconnectRetryTimeoutRef.current = null
          if (canPerformOperation('reconnect')) {
            attemptReconnection()
          }
        }, retryDelayMs)
      } else {
        resetReconnectionAttempts()
        if (isServerKick) {
          console.warn(`[LiveKit] Server-initiated disconnect (${reason}), not auto-reconnecting`)
          // Sync UI: transition to dashboard so user doesn't see room with lingering audio
          store.dispatch(setRoomJoined(false))
        }
      }

      // Destroy denoiser processor on disconnect
      if (denoiserProcessorRef.current) {
        try {
          denoiserProcessorRef.current.destroy()
        } catch (e) {
          console.warn('[LiveKit] Error destroying denoiser processor on disconnect:', e)
        }
        denoiserProcessorRef.current = null
      }

      // Guard track cleanup: only attempt if the room's internal state
      // indicates the peer connection may still be usable. When the peer
      // connection is already closed, calling unpublishTrack / track.stop()
      // triggers "could not createOffer with closed peer connection" errors.
      const roomState = room.state as string
      const peerConnectionAlive = roomState !== ConnectionState.Disconnected
      try {
        const localParticipant = room.localParticipant
        if (localParticipant && peerConnectionAlive) {
          const screenShareTracks = Array.from(localParticipant.trackPublications.values())
            .filter((pub) => pub.source === Track.Source.ScreenShare || pub.source === Track.Source.ScreenShareAudio)

          screenShareTracks.forEach((publication) => {
            try {
              if (publication.track) {
                publication.track.stop()
              }
            } catch (error) {
              console.warn('[LiveKit] Error stopping track on disconnect:', error)
            }
          })
        }
        setScreenShareEnabled(false)
      } catch (error) {
        console.warn('[LiveKit] Error during disconnect cleanup:', error)
      }

      dispatchConnectionStatus(ConnectionState.Disconnected, room)
      // RC5: only fire the failsafe-error event when the room is truly and
      // finally disconnected (not during a transient RECONNECTING phase).
      // Check that the reason is non-CLIENT_INITIATED AND the room's
      // state is genuinely Disconnected (not Reconnecting).
      const isHardDisconnect =
        reason &&
        reason !== DisconnectReason.CLIENT_INITIATED &&
        room.state === ConnectionState.Disconnected
      if (isHardDisconnect) {
        const disconnectMessage =
          'Video call disconnected unexpectedly.'
        if (willAutoRetry) {
          dispatchLivekitReconnectUx(decideHardDisconnectAutoRetryPending())
        } else {
          window.dispatchEvent(
            new CustomEvent('livekit-failsafe-error', {
              detail: { message: disconnectMessage },
            })
          )
          dispatchLivekitReconnectUx(decideHardDisconnectNoAutoRetry(disconnectMessage), { forceModal: true })
        }
      }
    }
    const handleReconnecting = () => {
      clearReconnectingWatchdog()
      // Update state machine
      transitionConnectionState(LiveKitConnectionStateMachine.RECONNECTING, 'Reconnecting')
      setIsReconnecting(true)
      // Call Stability Fix: Show persistent banner so user knows media is reconnecting
      store.dispatch(setLiveKitReconnectState({ showPrompt: true, message: 'Media reconnecting...' }))
      window.dispatchEvent(
        new CustomEvent(LIVEKIT_RECONNECTION_EVENT, { detail: { isReconnecting: true } })
      )
      const lkUxWatchdog = getLivekitReconnectUxConfig()
      reconnectingWatchdogRef.current = setTimeout(() => {
        reconnectingWatchdogRef.current = null
        if (connectionStateRef.current !== LiveKitConnectionStateMachine.RECONNECTING) {
          return
        }
        console.warn('[LiveKit] Reconnecting watchdog: no progress — forcing disconnect')
        try {
          room.disconnect()
        } catch (e) {
          console.warn('[LiveKit] Watchdog disconnect error:', e)
        }
        dispatchLivekitReconnectUx(
          decideReconnectingWatchdog(
            reconnectAttemptsRef.current,
            lkUxWatchdog.maxAutoReconnectAttempts
          ),
          { forceModal: true }
        )
      }, lkUxWatchdog.reconnectingWatchdogMs)
    }
    const handleReconnected = () => {
      clearReconnectingWatchdog()
      clearDelayedDisconnectRetry()
      postReconnectVerifyGenRef.current += 1
      const verifyGen = postReconnectVerifyGenRef.current
      poorConnectionStreakRef.current = 0
      // Update state machine
      transitionConnectionState(LiveKitConnectionStateMachine.CONNECTED, 'Reconnected')
      setIsReconnecting(false)
      resetReconnectionAttempts() // Reset on successful reconnection
      resetLivekitReconnectUxPolicy()
      const roomId = roomDataRef.current?.colyseusRoomId
      trackEvent(
        'APP_livekit_reconnected',
        stripUndefinedAnalyticsProps({
          ...livekitAnalyticsBaseProps(roomId, roomDataRef.current?.roomName),
        }),
        'Livekit'
      )
      // SC-257: a reconnect that completes while the tab is backgrounded can land with the
      // audio context still suspended — resume it so remote voice returns without a reload.
      void tryResumeAudioPlayback(room, 'reconnected')
      // SC-304: resume the denoiser's AudioContext so outbound audio is restored.
      void denoiserProcessorRef.current?.resumeContext()
      // SC-304: force proximity recalculation so remote track subscriptions are rebuilt
      // immediately rather than waiting for the next debounced cycle (which may miss due
      // to stale caches or timing). The subscription controller clears its state on
      // Reconnecting→Connected, but the proximity system needs a kick to re-evaluate.
      window.dispatchEvent(new CustomEvent('force-proximity-recalculation'))
      // SC-304: if Colyseus is dead after LiveKit reconnects, log a warning.
      // The subscription controller handles track cleanup via Redux connectionStatus.
      const colyseusStatus = store.getState().user.connectionStatus
      if (colyseusStatus === ConnectionStatus.OFFLINE) {
        console.warn(
          '[LiveKit] Reconnected but Colyseus is OFFLINE — subscriptions will be paused until Colyseus reconnects'
        )
      }
      store.dispatch(
        setLiveKitReconnectState({
          showPrompt: false,
          message: '',
          showReconnectionDialog: false,
          cooldownUntil: undefined,
          reconnectAttempt: undefined,
          maxAttempts: undefined,
        })
      )

      // Restore device states after reconnection (missing = enabled), with retry on timeout
      // Call Stability Fix: 800ms delay and 3 retries for more reliable restoration
      if (room.localParticipant) {
        setTimeout(() => {
          isRestoringDevicesRef.current = true
          const r = room
          if (!r?.localParticipant) {
            isRestoringDevicesRef.current = false
            return
          }
          const savedVideoEnabled = localStorage.getItem('preferVideoEnabled') !== 'false'
          const savedAudioEnabled = localStorage.getItem('preferAudioEnabled') !== 'false'
          const startupMediaPlan = selectStartupMediaPlan({
            connectionProfile: connectionProfileRef.current,
            measuredRttMs: serverRttHintRef.current,
            userPrefersVideo: savedVideoEnabled,
          })
          const hasCam = r.localParticipant.isCameraEnabled
          const wantVideo =
            hasCam ||
            (savedVideoEnabled &&
              !networkVideoSuppressedRef.current &&
              !startupMediaPlan.keepVideoSuppressedUntilManual)
          // RC2: Read fresh device IDs from localStorage (same Bug-D fix already in handleConnected)
          const freshAudioDevice = localStorage.getItem('preferredAudioDevice') || preferredAudioDevice || undefined
          const freshVideoDevice = localStorage.getItem('preferredVideoDevice') || undefined
          const isPublishTimeoutError = (err: unknown) => {
            const msg = (err as Error)?.message ?? String(err)
            return /engine not connected|timeout|PublishTrackError/i.test(msg)
          }
          const RETRY_DELAYS_MS = startupMediaPlan.publishRetryDelaysMs
          const maxRetries = Math.max(0, RETRY_DELAYS_MS.length - 1)

          if (!wantVideo) {
            clearDelayedCameraEnable()
            syncEffectiveVideoState(false)
            if (hasCam) {
              r.localParticipant.setCameraEnabled(false).catch((err) => {
                console.error('[LiveKit] Failed to disable camera on reconnect:', err)
              })
            }
          }

          // SC-377: as on connect, a mic saved off is not opened. A mic muted in
          // game is still published: a resume keeps it, and a full reconnect
          // republishes it muted, so there is nothing to restore, and opening
          // it first would make it live for a moment.
          const tryMic = (attempt: number) => {
            const delayMs = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]
            const run = () => {
              // RC2: Use freshAudioDevice (from localStorage) instead of stale closure value
              enableMicWithDenoiser(r, freshAudioDevice)
                .catch((err) => {
                  if (isPublishTimeoutError(err) && attempt < maxRetries) {
                    tryMic(attempt + 1)
                  } else {
                    console.warn('[LiveKit] Failed to restore microphone after reconnection:', err)
                    // Sync UI state so buttons reflect the actual track state
                    setAudioEnabled(false)
                    getBootstrapNetwork()?.room?.send(Message.AUDIO_DISCONNECTED)
                    window.dispatchEvent(
                      new CustomEvent(LIVEKIT_AUDIO_STATE_CHANGED, { detail: { enabled: false } })
                    )
                    window.dispatchEvent(
                      new CustomEvent('livekit-device-state', {
                        detail: {
                          cameraEnabled: r.localParticipant.isCameraEnabled,
                          microphoneEnabled: false,
                        },
                      })
                    )
                  }
                })
            }
            if (delayMs === 0) run()
            else setTimeout(run, delayMs)
          }
          if (savedAudioEnabled) tryMic(0)

          if (wantVideo) {
            scheduleCameraEnable(
              r,
              freshVideoDevice || undefined,
              RETRY_DELAYS_MS,
              startupMediaPlan.cameraEnableDelayMs
            )
          }

          const trackVerifyResolved = { ok: false }
          const lkUxVerify = getLivekitReconnectUxConfig()
          for (let i = 0; i < lkUxVerify.postReconnectTrackVerifyRetries; i++) {
            const delayMs =
              lkUxVerify.postReconnectTrackVerifyDelayMs +
              i * lkUxVerify.postReconnectTrackVerifyIntervalMs
            setTimeout(() => {
              if (verifyGen !== postReconnectVerifyGenRef.current) return
              if (trackVerifyResolved.ok) return
              const lp = r.localParticipant
              if (!lp || r.state !== ConnectionState.Connected) return
              const wantVideo =
                localStorage.getItem('preferVideoEnabled') !== 'false' &&
                !networkVideoSuppressedRef.current
              const wantAudio = localStorage.getItem('preferAudioEnabled') !== 'false'
              const pubs = Array.from(lp.trackPublications.values()) as LocalTrackPublication[]
              const hasCam = pubs.some((p) => p.source === Track.Source.Camera && !!p.track)
              const hasMic = pubs.some((p) => p.source === Track.Source.Microphone && !!p.track)
              if ((!wantVideo || hasCam) && (!wantAudio || hasMic)) {
                trackVerifyResolved.ok = true
                return
              }
              const isLast = i === lkUxVerify.postReconnectTrackVerifyRetries - 1
              if (!isLast) return
              const net = getBootstrapNetwork()
              const tok = roomDataRef.current?.token
              if (net && tok && net.isLiveKitJwtDueRefresh(tok)) {
                void net.requestLiveKitTokenAndWait(20000)
              }
              const missingCam = wantVideo && !hasCam
              const missingMic = wantAudio && !hasMic
              if (missingCam && missingMic) {
                dispatchLivekitReconnectUx(decidePostReconnectBothTracksMissing(), { forceModal: true })
              } else if (missingCam) {
                dispatchLivekitReconnectUx(decidePostReconnectTrackMissing('camera'), { forceModal: true })
              } else if (missingMic) {
                dispatchLivekitReconnectUx(decidePostReconnectTrackMissing('microphone'), { forceModal: true })
              }
            }, delayMs)
          }

          if (screenShareEnabled) {
            r.localParticipant.setScreenShareEnabled(true, SCREEN_SHARE_CAPTURE_OPTIONS).catch((err) => {
              console.warn('[LiveKit] Failed to restore screen share after reconnection:', err)
              setScreenShareEnabled(false)
            })
          }

          window.dispatchEvent(
            new CustomEvent(LIVEKIT_AUDIO_STATE_CHANGED, { detail: { enabled: savedAudioEnabled } })
          )
          window.dispatchEvent(
            new CustomEvent(LIVEKIT_VIDEO_STATE_CHANGED, { detail: { enabled: wantVideo } })
          )
          connectionQualityRef.current = 'unknown'
          // Clear restore flag after async restore completes (upper bound for all tryMic/tryCamera paths)
          setTimeout(() => {
            isRestoringDevicesRef.current = false
          }, 10000)
        }, 1500)
      }

      window.dispatchEvent(
        new CustomEvent(LIVEKIT_RECONNECTION_EVENT, { detail: { isReconnecting: false } })
      )
    }
    const handleConnectionStateChanged = (state: ConnectionState) => {
      dispatchConnectionStatus(state, room)
    }

    // RC3: Handle local track being unpublished unexpectedly (e.g. device revoked by OS).
    // If the room is still connected and the user hasn't manually disabled that media type,
    // attempt to republish automatically and surface an in-app notification.
    const handleLocalTrackUnpublished = (publication: LocalTrackPublication) => {
      const isCam = publication.source === Track.Source.Camera
      const isMic = publication.source === Track.Source.Microphone
      const isScreenShare = publication.source === Track.Source.ScreenShare

      // When a screen-share track is unpublished externally (e.g. user clicked
      // the native browser/OS "Stop sharing" button), reset the UI button state
      // so it no longer shows "stop sharing".
      if (isScreenShare && screenShareEnabledRef.current) {
        setScreenShareEnabled(false)
        window.dispatchEvent(
          new CustomEvent(LIVEKIT_SCREEN_SHARE_STATE_CHANGED, { detail: { enabled: false } })
        )
      }

      if (!isCam && !isMic) return

      if (isCam) backgroundLifecycle.handleCameraUnpublished(room, publication)

      // SC-158: taken down on purpose — a held camera whose effect cannot be
      // shown. Starting it again here would bypass the camera-start gate.
      if (isCam && intentionalCameraUnpublishRef.current) return

      // Only attempt re-publish if the room is still connected
      if (room.state !== ConnectionState.Connected) return

      const userPreferredVideo = localStorage.getItem('preferVideoEnabled') !== 'false'
      const userPreferredAudio = localStorage.getItem('preferAudioEnabled') !== 'false'

      if (isCam && userPreferredVideo && !networkVideoSuppressedRef.current) {
        const notifyMessage = 'Camera stopped unexpectedly — attempting to restart.'
        console.warn('[LiveKit] RC3: Camera track unpublished unexpectedly, republishing...', notifyMessage)
        store.dispatch(addNotification({ type: 'warning', message: notifyMessage, duration: 6000 }))
        store.dispatch(
          setLiveKitReconnectState({ showPrompt: true, showReconnectionDialog: false, message: notifyMessage })
        )
        const freshVideoDevice = localStorage.getItem('preferredVideoDevice') || undefined
        setTimeout(() => {
          if (cameraEnableInProgressRef.current) {
            console.warn('[LiveKit] RC3: camera enable already in progress — skipping recovery republish')
            return
          }
          const opId = ++cameraEnableOpIdRef.current
          cameraEnableInProgressRef.current = true
          // SC-158: a new camera track, so the same gate as every other start.
          void backgroundLifecycle.prepareCameraStart(room)
            .then((mayStart) => {
              if (!mayStart) return undefined
              // Turned off while the gate waited for the effect.
              if (localStorage.getItem('preferVideoEnabled') === 'false') return undefined
              // SC-158 (review #11): the full capture options, built once the
              // gate has answered, like every other start site. A bare deviceId
              // left LiveKit to fill in the join-time 360p defaults, so a camera
              // republished with an effect lost the 720p the effect needs.
              const cameraOptions = getVideoCaptureDefaults(freshVideoDevice, captureVideoPreset())
              return room.localParticipant.setCameraEnabled(true, cameraOptions).then(() => true)
            })
            .catch((err) => {
              console.error('[LiveKit] RC3: Failed to republish camera:', err)
              // SC-158 (E10): failed for a camera the user has turned off since.
              if (localStorage.getItem('preferVideoEnabled') === 'false') {
                backgroundLifecycle.forgetCameraStart(room)
                return false
              }
              // SC-158: a background that would not start has its own notice,
              // and is not the device's fault.
              if (!backgroundLifecycle.handleCameraStartFailed(room)) {
                store.dispatch(addNotification({ type: 'error', message: 'Camera could not restart. Please check your device.', duration: 8000 }))
              }
              return false
            })
            .finally(() => {
              if (cameraEnableOpIdRef.current === opId) {
                cameraEnableInProgressRef.current = false
              }
            })
            .then((started) => {
              if (!started) return undefined
              // SC-158 (E10): the user may have turned the camera off, or off
              // and on, while this republish waited for its background.
              const followUp = decideCameraFollowUp({
                wantsOn: localStorage.getItem('preferVideoEnabled') !== 'false',
                cameraOn: room.localParticipant.isCameraEnabled,
                startRunning: cameraEnableInProgressRef.current,
                videoSuppressed: networkVideoSuppressedRef.current,
              })
              if (followUp === 'turn-off') return room.localParticipant.setCameraEnabled(false)
              if (followUp === 'start') {
                scheduleCameraEnableRef.current?.(
                  room,
                  freshVideoDevice,
                  BACKGROUND_READY_CAMERA_RETRY_DELAYS_MS,
                  0
                )
              }
              return undefined
            })
            .catch((err) => {
              console.error('[LiveKit] RC3: Failed to turn the camera off again:', err)
            })
        }, 1000)
      }

      if (isMic && userPreferredAudio) {
        const notifyMessage = 'Microphone stopped unexpectedly — attempting to restart.'
        console.warn('[LiveKit] RC3: Mic track unpublished unexpectedly, republishing...')
        store.dispatch(addNotification({ type: 'warning', message: notifyMessage, duration: 6000 }))
        store.dispatch(
          setLiveKitReconnectState({ showPrompt: true, showReconnectionDialog: false, message: notifyMessage })
        )
        const freshAudioDevice = localStorage.getItem('preferredAudioDevice') || preferredAudioDevice || undefined
        setTimeout(() => {
          enableMicWithDenoiser(room, freshAudioDevice).catch((err) => {
            console.error('[LiveKit] RC3: Failed to republish mic:', err)
            store.dispatch(addNotification({ type: 'error', message: 'Microphone could not restart. Please check your device.', duration: 8000 }))
          })
        }, 1000)
      }
    }

    // SC-158: keep the chosen background on every newly published camera track
    // (and take it off one that came up with an effect the user no longer
    // wants). Screen share is ignored inside.
    //
    // Hooking the event rather than each setCameraEnabled(true) call site is
    // deliberate: a camera track is published from several paths — the initial
    // connect, toggleVideo when no camera publication exists yet, the RC3
    // auto-republish above, and a full reconnect republishing the same track.
    // (Turning the camera off and on mutes and unmutes the existing track, and
    // a device switch restarts it; neither publishes.) One event covers them
    // all, including any path added later.
    const handleLocalTrackPublished = (publication: LocalTrackPublication) => {
      void backgroundLifecycle.handleLocalTrackPublished(room, publication)
    }
    // SC-158: a camera's sender exists before it is negotiated; nothing goes on
    // it until the effect has produced a composited frame.
    const handleLocalSenderCreated = (sender: RTCRtpSender, track: unknown) => {
      void backgroundLifecycle.handleLocalSenderCreated(room, sender, track)
    }
    const handleLocalTrackUnmuted = (publication: unknown) => {
      const pub = publication as LocalTrackPublication
      if (pub?.source !== Track.Source.Camera) return
      void backgroundLifecycle.handleCameraUnmuted(room, pub)
    }

    const handleTrackSubscriptionFailed = (trackSid: string, _participant?: any) => {
      console.warn('[LiveKit] Track subscription failed:', trackSid)
      store.dispatch(addNotification({
        type: 'warning',
        message: 'Could not load a remote stream. Connection may be unstable.',
        duration: 6000,
      }))
    }

    // SC-257: the browser can suspend/block remote audio playback (webAudioMix
    // AudioContext suspended in a background tab, or autoplay-blocked elements). On the
    // status change, try to resume programmatically; if that's blocked, show the
    // click-to-resume affordance. Analytics let us measure this in the field — it never
    // appears in disconnect metrics because the room stays Connected throughout.
    const handleAudioPlaybackStatusChanged = () => {
      const transition = decideAudioPlaybackTransition(audioPlaybackBlockedRef.current, room.canPlaybackAudio)
      if (transition.event === null) return
      audioPlaybackBlockedRef.current = transition.blocked
      setAudioPlaybackBlocked(transition.blocked)
      if (transition.event === 'blocked') {
        trackEvent(
          'APP_livekit_audio_playback_blocked',
          stripUndefinedAnalyticsProps({
            ...livekitAnalyticsBaseProps(roomDataRef.current?.colyseusRoomId, roomDataRef.current?.roomName),
          }),
          'Livekit'
        )
        // Best-effort auto-resume; if the browser blocks it (no gesture) the affordance stays.
        void tryResumeAudioPlayback(room, 'playback-status-changed')
      } else {
        trackEvent(
          'APP_livekit_audio_playback_resumed',
          stripUndefinedAnalyticsProps({
            source: lastAudioResumeSourceRef.current,
            ...livekitAnalyticsBaseProps(roomDataRef.current?.colyseusRoomId, roomDataRef.current?.roomName),
          }),
          'Livekit'
        )
        lastAudioResumeSourceRef.current = 'auto'
      }
    }

    room.on(RoomEvent.Connected, handleConnected)
    room.on(RoomEvent.Disconnected, handleDisconnected)
    room.on(RoomEvent.Reconnecting, handleReconnecting)
    room.on(RoomEvent.Reconnected, handleReconnected)
    room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged)
    room.on(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished)
    room.on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished)
    room.localParticipant.on(ParticipantEvent.LocalSenderCreated, handleLocalSenderCreated)
    room.localParticipant.on(ParticipantEvent.TrackUnmuted, handleLocalTrackUnmuted)
    room.on(RoomEvent.TrackSubscriptionFailed, handleTrackSubscriptionFailed)
    room.on(RoomEvent.AudioPlaybackStatusChanged, handleAudioPlaybackStatusChanged)
    return () => {
      clearReconnectingWatchdog()
      clearDelayedDisconnectRetry()
      if (deviceEnableTimeoutRef.current) {
        clearTimeout(deviceEnableTimeoutRef.current)
        deviceEnableTimeoutRef.current = null
      }
      clearDelayedCameraEnable()
      ++cameraEnableOpIdRef.current
      cameraEnableInProgressRef.current = false
      room.off(RoomEvent.Connected, handleConnected)
      room.off(RoomEvent.Disconnected, handleDisconnected)
      room.off(RoomEvent.Reconnecting, handleReconnecting)
      room.off(RoomEvent.Reconnected, handleReconnected)
      room.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged)
      room.off(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished)
      room.off(RoomEvent.LocalTrackPublished, handleLocalTrackPublished)
      room.localParticipant.off(ParticipantEvent.LocalSenderCreated, handleLocalSenderCreated)
      room.localParticipant.off(ParticipantEvent.TrackUnmuted, handleLocalTrackUnmuted)
      room.off(RoomEvent.TrackSubscriptionFailed, handleTrackSubscriptionFailed)
      room.off(RoomEvent.AudioPlaybackStatusChanged, handleAudioPlaybackStatusChanged)
    }
  }, [room, preferredAudioDevice, enableMicWithDenoiser, muteLocalMicrophone, clearReconnectingWatchdog, clearDelayedDisconnectRetry, clearDelayedCameraEnable, scheduleCameraEnable, syncEffectiveVideoState, tryResumeAudioPlayback, captureVideoPreset])

  // Page visibility handler for preventing disconnections on dialogs
  const pageLeaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const dialogOpenTimeRef = useRef<number | null>(null)

  useEffect(() => {
    // RC1 + Call Stability Fix: 15s grace period for dialogs/alerts.
    // pagehide is treated as a soft signal because Safari may emit it while
    // backgrounding or parking the page in BFCache.
    const GRACE_PERIOD_MS = 15000 // 15 second grace period for dialogs/alerts

    const handlePageLeaveDetected = (event: CustomEvent) => {
      const currentState = connectionStateRef.current

      console.log('📱 [LiveKit] Page leave detected, checking connection state', {
        room: roomData?.roomName,
        roomID: roomData?.colyseusRoomId,
        participant: room?.localParticipant?.identity,
        pID: room?.localParticipant?.sid,
        isConnected: roomConnected,
        roomState: room?.state,
        connectionState: currentState
      })

      // Don't disconnect if we're already disconnecting or disconnected
      if (currentState === LiveKitConnectionStateMachine.DISCONNECTING ||
        currentState === LiveKitConnectionStateMachine.DISCONNECTED) {
        console.log('📱 [LiveKit] Skipping disconnection - already disconnecting/disconnected')
        return
      }

      // Only disconnect if we're actually connected
      if (!room || !roomConnected || room.state !== 'connected') {
        console.log('📱 [LiveKit] Skipping disconnection - room not connected')
        return
      }

      // Skip teardown while a dialog/modal keeps the user in the room. SC-246: the
      // whiteboard is a long-lived in-page iframe modal, so rely on the live
      // "is a dialog open" flag (protects for the modal's whole lifetime) in addition
      // to the legacy fixed-window grace (which lapses 15s after the last dialog event).
      const now = Date.now()
      const withinDialogGrace =
        !!dialogOpenTimeRef.current && (now - dialogOpenTimeRef.current) < GRACE_PERIOD_MS
      const dialogOpen = pageVisibilityHandler.isDialogOpen()
      if (shouldDeferPageLeaveDisconnect({ dialogOpen, withinDialogGrace })) {
        console.log('📱 [LiveKit] Dialog open or within grace period, skipping disconnection', {
          dialogOpen,
          withinDialogGrace,
        })
        // Clear any pending timeout
        if (pageLeaveTimeoutRef.current) {
          clearTimeout(pageLeaveTimeoutRef.current)
          pageLeaveTimeoutRef.current = null
        }
        return
      }

      // Clear any existing timeout
      if (pageLeaveTimeoutRef.current) {
        clearTimeout(pageLeaveTimeoutRef.current)
      }

      // Confirmed page leave disconnects immediately; pagehide waits for the
      // grace period so temporary backgrounding on Safari does not tear down media.
      const disconnectDelayMs = getPageLeaveDisconnectDelayMs(
        ((event as CustomEvent).detail?.reason ?? 'page-leave') as LiveKitPageLeaveReason,
        GRACE_PERIOD_MS
      )
      pageLeaveTimeoutRef.current = setTimeout(() => {
        if (pageVisibilityHandler.isPageVisible()) {
          console.log('📱 [LiveKit] Page became visible again, canceling pending disconnect')
          pageLeaveTimeoutRef.current = null
          return
        }

        // Double-check state before disconnecting
        if (room && roomConnected && room.state === 'connected' &&
          connectionStateRef.current === LiveKitConnectionStateMachine.CONNECTED) {
          console.log('📱 [LiveKit] Disconnecting from room due to confirmed page leave')

          if (canPerformOperation('disconnect')) {
            transitionConnectionState(LiveKitConnectionStateMachine.DISCONNECTING, 'Page leave confirmed')
            room.disconnect()
            setRoom(null)
            setRoomConnected(false)
            transitionConnectionState(LiveKitConnectionStateMachine.DISCONNECTED, 'Disconnected due to page leave')
          }
        }
        pageLeaveTimeoutRef.current = null
      }, disconnectDelayMs)
    }

    // Listen for dialog events to track when dialogs are opened
    const handleDialogOpened = () => {
      dialogOpenTimeRef.current = Date.now()
      // Clear any pending disconnect timeout
      if (pageLeaveTimeoutRef.current) {
        clearTimeout(pageLeaveTimeoutRef.current)
        pageLeaveTimeoutRef.current = null
      }
    }

    const handleDialogClosed = () => {
      // Keep the timestamp for a bit longer to handle rapid open/close
      dialogOpenTimeRef.current = Date.now()
    }

    const handlePageVisible = () => {
      if (pageLeaveTimeoutRef.current) {
        clearTimeout(pageLeaveTimeoutRef.current)
        pageLeaveTimeoutRef.current = null
      }
      // SC-257: the tab regained focus — resume audio the browser suspended while hidden.
      void tryResumeAudioPlayback(roomRef.current, 'page-visible')
      // SC-304: resume the denoiser's AudioContext (outbound audio pipeline) which
      // the browser suspends independently of LiveKit's webAudioMix context.
      void denoiserProcessorRef.current?.resumeContext()
    }

    // SC-257: also cover the Page Lifecycle 'resume' (unfreeze) and raw window focus
    // (e.g. Cmd/Ctrl+Tab, where the context can be suspended without a visibilitychange).
    const handlePageResumed = () => {
      void tryResumeAudioPlayback(roomRef.current, 'page-resumed')
      void denoiserProcessorRef.current?.resumeContext()
    }
    const handleWindowFocus = () => {
      void tryResumeAudioPlayback(roomRef.current, 'window-focus')
      void denoiserProcessorRef.current?.resumeContext()
    }

    window.addEventListener('livekit-page-leave-detected', handlePageLeaveDetected as EventListener)
    window.addEventListener('page-visible', handlePageVisible)
    window.addEventListener('page-resumed', handlePageResumed)
    window.addEventListener('focus', handleWindowFocus)
    window.addEventListener('dialog-opened', handleDialogOpened)
    window.addEventListener('modal-opened', handleDialogOpened)
    window.addEventListener('alert-opened', handleDialogOpened)
    window.addEventListener('dialog-closed', handleDialogClosed)
    window.addEventListener('modal-closed', handleDialogClosed)
    window.addEventListener('alert-closed', handleDialogClosed)

    return () => {
      window.removeEventListener('livekit-page-leave-detected', handlePageLeaveDetected as EventListener)
      window.removeEventListener('page-visible', handlePageVisible)
      window.removeEventListener('page-resumed', handlePageResumed)
      window.removeEventListener('focus', handleWindowFocus)
      window.removeEventListener('dialog-opened', handleDialogOpened)
      window.removeEventListener('modal-opened', handleDialogOpened)
      window.removeEventListener('alert-opened', handleDialogOpened)
      window.removeEventListener('dialog-closed', handleDialogClosed)
      window.removeEventListener('modal-closed', handleDialogClosed)
      window.removeEventListener('alert-closed', handleDialogClosed)

      // Clear timeout on cleanup
      if (pageLeaveTimeoutRef.current) {
        clearTimeout(pageLeaveTimeoutRef.current)
        pageLeaveTimeoutRef.current = null
      }
      dialogOpenTimeRef.current = null
    }
  }, [room, roomData, roomConnected, canPerformOperation, transitionConnectionState, tryResumeAudioPlayback])

  // Device toggles: mute/unmute keeps track published to avoid "local track subscription" / reconnection loop.
  // Fix 3: Validates the underlying MediaStreamTrack is still alive before unmuting;
  // if it's "ended" (AudioContext died, device disconnected, etc.), creates a fresh pipeline.
  const toggleAudio = useCallback(
    async (enabled: boolean) => {
      if (!room) {
        // The server still counts the mic as on from before LiveKit went away,
        // and a reconnect with the mic saved off never tells it otherwise.
        if (!enabled) getBootstrapNetwork()?.room?.send(Message.AUDIO_DISCONNECTED)
        return
      }
      micWantedOnRef.current = enabled
      // An enable in progress applies this when it finishes (SC-377). Acting now
      // would mute through LiveKit's own mute before the denoiser patches it,
      // and the next unmute would be silent.
      if (micEnableInProgressRef.current) {
        // The enable records it, even inside the restore window.
        micUserTouchedRef.current = true
        return
      }

      const sendScreenShareSessionEvent = (messageType: Message, sessionId: string) => {
        try {
          const phaserGame = getPhaserGameSync()
          const game = phaserGame?.scene?.keys?.game as Game | undefined
          game?.network?.room?.send(messageType, { sessionId })
        } catch (e) {
          console.warn(
            `[LiveKit] Failed to send ${messageType === Message.START_SCREEN_SHARE_SESSION ? 'START' : 'END'}_SCREEN_SHARE_SESSION:`,
            e
          )
        }
      }

      const resetScreenShareUiState = () => {
        setScreenShareEnabled(false)
        window.dispatchEvent(
          new CustomEvent(LIVEKIT_SCREEN_SHARE_STATE_CHANGED, { detail: { enabled: false } })
        )
      }

      const cleanupFailedScreenShareStart = () => {
        if (enabled && screenShareSessionIdRef.current) {
          sendScreenShareSessionEvent(Message.END_SCREEN_SHARE_SESSION, screenShareSessionIdRef.current)
          screenShareSessionIdRef.current = ''
        }

        releaseComputerScreenShare(getBootstrapNetwork(), activeComputerIdRef.current)
        activeComputerIdRef.current = null

        resetScreenShareUiState()
      }
      try {
        if (enabled) {
          const pub = getLocalMicPublication(room)
          // Fix 3: Check that the publication has a track AND the underlying
          // MediaStreamTrack is still in "live" readyState. If the AudioContext
          // died or the device was disconnected, the track becomes "ended" and
          // unmuting it would produce silence or crackling.
          const underlyingTrack = pub?.track?.mediaStreamTrack
          const isTrackAlive = underlyingTrack && underlyingTrack.readyState === 'live'

          if (pub && pub.track && isTrackAlive) {
            await unmuteLocalMicrophone(room)
            setAudioEnabled(true)
            trackEvent(
              'APP_livekit_audio_toggled',
              stripUndefinedAnalyticsProps({
                enabled: true,
                ...livekitAnalyticsBaseProps(roomDataRef.current?.colyseusRoomId, roomDataRef.current?.roomName),
              }),
              'Livekit'
            )
            getBootstrapNetwork()?.room?.send(Message.AUDIO_CONNECTED)
            window.dispatchEvent(
              new CustomEvent('livekit-device-state', {
                detail: {
                  cameraEnabled: room.localParticipant.isCameraEnabled,
                  microphoneEnabled: true,
                },
              })
            )
            window.dispatchEvent(
              new CustomEvent(LIVEKIT_AUDIO_STATE_CHANGED, { detail: { enabled: true } })
            )
            dispatchConnectionStatus(room.state as ConnectionState, room)
          } else {
            // No published track, or underlying track is dead — create a fresh pipeline
            if (pub?.track && !isTrackAlive) {
              console.warn('[LiveKit] Mic track underlying MediaStreamTrack is ended, recreating pipeline')
            }
            // Re-read device ID from localStorage to get the freshest value
            const freshAudioDevice = localStorage.getItem('preferredAudioDevice') || preferredAudioDevice || undefined
            try {
              await enableMicWithDenoiser(room, freshAudioDevice, { userInitiated: true })
            } catch (error) {
              // The call bar turned itself on at the click. With a mic saved off
              // this is the first time it is opened, so a refused permission
              // lands here, and nothing else would turn the button back.
              window.dispatchEvent(
                new CustomEvent(LIVEKIT_AUDIO_STATE_CHANGED, { detail: { enabled: false } })
              )
              // The click saved the mic on. Left so, the next connect or
              // reconnect would open it again on its own. A refused permission
              // is a forced off, so the next Join screen asks again (the
              // classifier is the camera's; the error names are the same). A
              // mute clicked meanwhile already saved the user's own off.
              if (micWantedOnRef.current) {
                writeMediaPreference('audio', false, { forced: isCameraPermissionError(error) })
              }
              throw error
            }
          }
        } else {
          const pub = getLocalMicPublication(room)
          if (pub && pub.track) {
            await muteLocalMicrophone(room)
            setAudioEnabled(false)
            trackEvent(
              'APP_livekit_audio_toggled',
              stripUndefinedAnalyticsProps({
                enabled: false,
                ...livekitAnalyticsBaseProps(roomDataRef.current?.colyseusRoomId, roomDataRef.current?.roomName),
              }),
              'Livekit'
            )
            getBootstrapNetwork()?.room?.send(Message.AUDIO_DISCONNECTED)
            window.dispatchEvent(
              new CustomEvent('livekit-device-state', {
                detail: {
                  cameraEnabled: room.localParticipant.isCameraEnabled,
                  microphoneEnabled: false,
                },
              })
            )
            window.dispatchEvent(
              new CustomEvent(LIVEKIT_AUDIO_STATE_CHANGED, { detail: { enabled: false } })
            )
            dispatchConnectionStatus(room.state as ConnectionState, room)
          } else {
            // Nothing to mute, but the server may still count the mic as on
            // (set before LiveKit dropped it). A repeat off is harmless there.
            getBootstrapNetwork()?.room?.send(Message.AUDIO_DISCONNECTED)
          }
        }
      } catch (error) {
        console.error('Error toggling microphone:', error)
      }
    },
    [
      room,
      preferredAudioDevice,
      getLocalMicPublication,
      muteLocalMicrophone,
      unmuteLocalMicrophone,
      enableMicWithDenoiser,
    ]
  )

  const toggleVideo = useCallback(
    async (enabled: boolean) => {
      if (!room) return
      // SC-374: cancel any pending scheduled camera enable to prevent races
      clearDelayedCameraEnable()
      if (enabled && cameraEnableInProgressRef.current) {
        console.warn('[LiveKit] toggleVideo: camera enable already in progress — skipping')
        return
      }
      try {
        if (enabled) {
          networkVideoSuppressedRef.current = false
          recoveryHintShownRef.current = false
        }

        // When enabling, pass the fresh device ID from localStorage so LiveKit
        // uses the correct camera even if no camera track was previously created
        // (e.g. video was disabled at join time).
        const freshDeviceId = enabled ? localStorage.getItem('preferredVideoDevice') || undefined : undefined

        if (enabled) {
          // SC-273 (B2): retry transient device-acquisition failures ("AbortError: Timeout
          // starting video source", NotReadableError) instead of silently leaving the camera
          // off. Attempts 2-3 fall back to the default device in case the stored preferred
          // deviceId is stale or briefly busy. Non-retryable errors (permission, constraints)
          // throw immediately.
          const opId = ++cameraEnableOpIdRef.current
          cameraEnableInProgressRef.current = true
          try {
            // SC-158: the same gate as every camera start. Held means the user
            // has been told why and the camera starts once the effect is ready.
            if (!(await backgroundLifecycle.prepareCameraStart(room))) {
              console.warn('[LiveKit] SC-158: camera held until the background effect can be shown')
              // Nothing is being rebuilt; the button must stay usable.
              endVideoToggleCooldown()
              return
            }
            // Turned off again while the gate waited for the effect.
            if (localStorage.getItem('preferVideoEnabled') === 'false') {
              endVideoToggleCooldown()
              return
            }
            // Built once the gate has answered, like every other start site: the
            // gate can wait seconds for the effect, and a "No effect" picked
            // meanwhile must start the camera at the profile capture, not at the
            // effect's 720p that this call saw on the way in.
            const cameraOptions = getVideoCaptureDefaults(freshDeviceId, captureVideoPreset())
            const fallbackOptions = getVideoCaptureDefaults(undefined, captureVideoPreset())
            const attempts = [cameraOptions, fallbackOptions, fallbackOptions]
            let lastError: unknown
            let enabledOk = false
            for (let i = 0; i < attempts.length; i++) {
              try {
                await room.localParticipant.setCameraEnabled(true, attempts[i])
                enabledOk = true
                break
              } catch (err) {
                lastError = err
                if (!isRetryableCameraStartError(err)) throw err
                console.warn(`[LiveKit] Camera start failed (attempt ${i + 1}/${attempts.length}), retrying:`, err)
                if (i < attempts.length - 1) {
                  await new Promise((resolve) => setTimeout(resolve, 400 * (i + 1)))
                }
              }
            }
            if (!enabledOk) throw lastError
          } finally {
            if (cameraEnableOpIdRef.current === opId) {
              cameraEnableInProgressRef.current = false
            }
          }
        } else {
          await room.localParticipant.setCameraEnabled(false)
        }

        // SC-158 (E10): a start can wait a long time for its background, and an
        // off made meanwhile waits for it (LiveKit), while a new on is turned
        // away by the guard above. Whatever settled, end in the user's last choice.
        const wantsOn = localStorage.getItem('preferVideoEnabled') !== 'false'
        const followUp = decideCameraFollowUp({
          wantsOn,
          cameraOn: room.localParticipant.isCameraEnabled,
          startRunning: cameraEnableInProgressRef.current,
          videoSuppressed: networkVideoSuppressedRef.current,
        })
        if (followUp === 'turn-off') await room.localParticipant.setCameraEnabled(false)
        if (followUp === 'start') void toggleVideo(true)
        // A start the user has since turned off, or that an off landed on, did
        // not turn the camera on: none of the "on" side effects.
        if (enabled && (!wantsOn || followUp === 'start')) return

        // If enabling, ensure simulcast is enabled for better quality adaptation
        if (enabled) {
          // Get the camera track publication
          const cameraPublication = Array.from(room.localParticipant.trackPublications.values())
            .find((pub) => pub.source === Track.Source.Camera)

          // Simulcast is automatically enabled when dynacast is true,
          // but we can verify it's working
          if (cameraPublication && isDebugMode()) {
            console.log('[LiveKit] Camera enabled with simulcast support')
          }
        }

        setVideoEnabled(enabled)
        if (enabled) {
          // A successful start clears any previous "blocked" state — the user may
          // have just granted access in the browser.
          window.dispatchEvent(
            new CustomEvent(LIVEKIT_CAMERA_PERMISSION_BLOCKED_EVENT, { detail: { blocked: false } })
          )
        }
        trackEvent(
          'APP_livekit_video_toggled',
          stripUndefinedAnalyticsProps({
            enabled,
            ...livekitAnalyticsBaseProps(roomDataRef.current?.colyseusRoomId, roomDataRef.current?.roomName),
          }),
          'Livekit'
        )
        window.dispatchEvent(
          new CustomEvent('livekit-device-state', {
            detail: {
              cameraEnabled: room.localParticipant.isCameraEnabled,
              microphoneEnabled: room.localParticipant.isMicrophoneEnabled,
            },
          })
        )
        dispatchConnectionStatus(room.state as ConnectionState, room)
      } catch (error) {
        console.error('Error toggling camera:', error)
        // A blocked camera cannot be fixed by pressing the button again — the
        // user has to change a browser permission. Say so, instead of leaving a
        // control that fails identically every time.
        if (isCameraPermissionError(error)) {
          window.dispatchEvent(
            new CustomEvent(LIVEKIT_CAMERA_PERMISSION_BLOCKED_EVENT, { detail: { blocked: true } })
          )
        }
        // Release the toggle immediately: the rebuild never started, so waiting
        // out the cooldown would just leave the button dead for no reason. Not
        // for a failed "off": like any "off", it leaves a running cooldown alone.
        if (enabled) endVideoToggleCooldown()
        // SC-158 (E10): a start the user has turned off since failed for a
        // camera nobody wants: no "couldn't start" notice, no warning icon.
        if (enabled && localStorage.getItem('preferVideoEnabled') === 'false') {
          backgroundLifecycle.forgetCameraStart(room)
          return
        }
        // Revert the optimistic UI update — useVideoDisplayState already set
        // videoEnabled to the target value; dispatch the corrected state so the
        // overlay reflects reality. Not when the background took the failure:
        // the camera is still wanted on, and waiting for its background.
        showFailedCameraStart(
          () => enabled && backgroundLifecycle.handleCameraStartFailed(room),
          () =>
            window.dispatchEvent(
              new CustomEvent(LIVEKIT_VIDEO_STATE_CHANGED, { detail: { enabled: !enabled } })
            )
        )
      }
    },
    [room, endVideoToggleCooldown, backgroundLifecycle]
  )

  const toggleScreenShare = useCallback(
    async (enabled: boolean) => {
      if (!room) return
      if (enabled && screenShareOperationInFlightRef.current) return

      const sendScreenShareSessionEvent = (messageType: Message, sessionId: string) => {
        try {
          const phaserGame = getPhaserGameSync()
          const game = phaserGame?.scene?.keys?.game as Game | undefined
          game?.network?.room?.send(messageType, { sessionId })
        } catch (e) {
          console.warn(
            `[LiveKit] Failed to send ${messageType === Message.START_SCREEN_SHARE_SESSION ? 'START' : 'END'}_SCREEN_SHARE_SESSION:`,
            e
          )
        }
      }

      const resetScreenShareUiState = () => {
        setScreenShareEnabled(false)
        window.dispatchEvent(
          new CustomEvent(LIVEKIT_SCREEN_SHARE_STATE_CHANGED, { detail: { enabled: false } })
        )
      }

      const cleanupFailedScreenShareStart = () => {
        if (enabled && screenShareSessionIdRef.current) {
          sendScreenShareSessionEvent(Message.END_SCREEN_SHARE_SESSION, screenShareSessionIdRef.current)
          screenShareSessionIdRef.current = ''
        }

        activeComputerIdRef.current = clearActiveComputerScreenShare(
          getBootstrapNetwork(),
          activeComputerIdRef.current
        )

        resetScreenShareUiState()
      }

      if (enabled) {
        screenShareOperationInFlightRef.current = true
      }

      // Connection state guard: Don't allow screen share operations during reconnection
      if (room.state === ConnectionState.Reconnecting || room.state === ConnectionState.Disconnected) {
        console.warn('[LiveKit] Cannot toggle screen share: connection not ready', room.state)
        if (enabled) {
          screenShareOperationInFlightRef.current = false
        }
        return
      }

      // Screen share session management
      if (enabled) {
        // Generate a new persistent session ID (or use inherited one from takeover)
        if (!screenShareSessionIdRef.current) {
          screenShareSessionIdRef.current = crypto.randomUUID()
        }
        const sessionId = screenShareSessionIdRef.current
        trackEvent(
          'APP_livekit_screen_share_started',
          stripUndefinedAnalyticsProps({
            ...livekitAnalyticsBaseProps(roomDataRef.current?.colyseusRoomId, roomDataRef.current?.roomName),
          }),
          'Livekit'
        )
        window.dispatchEvent(
          new CustomEvent(LIVEKIT_SCREEN_SHARE_SESSION_ID_EVENT, { detail: { sessionId } })
        )
      } else {
        trackEvent(
          'APP_livekit_screen_share_ended',
          stripUndefinedAnalyticsProps({
            ...livekitAnalyticsBaseProps(roomDataRef.current?.colyseusRoomId, roomDataRef.current?.roomName),
          }),
          'Livekit'
        )
        sendScreenShareSessionEvent(Message.END_SCREEN_SHARE_SESSION, screenShareSessionIdRef.current)
        screenShareSessionIdRef.current = ''
        activeComputerIdRef.current = clearActiveComputerScreenShare(
          getBootstrapNetwork(),
          activeComputerIdRef.current,
          { notifyStop: true }
        )
      }

      // Check connection quality before starting screen share
      if (enabled) {
        const connectionQuality = room.localParticipant?.connectionQuality
        if (connectionQuality === 'poor' || connectionQuality === 'unknown') {
          console.warn('[LiveKit] Connection quality is poor, screen share may be unstable')
        }

        // Reduce camera quality to save bandwidth for screen share
        try {
          const cameraTrack = Array.from(room.localParticipant.trackPublications.values())
            .find((pub) => pub.source === Track.Source.Camera && pub.track)

          if (cameraTrack?.track) {
            const videoTrack = cameraTrack.track as any
            const settings = videoTrack.getSettings?.() || {}

            // Store original quality if not already stored
            if (!originalCameraQualityRef.current) {
              originalCameraQualityRef.current = {
                width: settings.width,
                height: settings.height,
                frameRate: settings.frameRate
              }
            }

            // Reduce camera quality per media config (screen-share reduction)
            const constraints = getScreenShareCameraReduction({
              width: settings.width,
              height: settings.height,
              frameRate: settings.frameRate,
            })

            videoTrack.applyConstraints?.(constraints).then(() => {
              isCameraQualityReducedRef.current = true
              console.log('[LiveKit] Reduced camera quality for screen share:', constraints)
            }).catch((err: Error) => {
              console.warn('[LiveKit] Failed to reduce camera quality:', err)
            })
          }
        } catch (error) {
          console.warn('[LiveKit] Error managing camera quality:', error)
        }
      } else {
        // Restore original camera quality when screen sharing stops
        if (isCameraQualityReducedRef.current && originalCameraQualityRef.current) {
          try {
            const cameraTrack = Array.from(room.localParticipant.trackPublications.values())
              .find((pub) => pub.source === Track.Source.Camera && pub.track)

            if (cameraTrack?.track) {
              const videoTrack = cameraTrack.track as any
              const originalQuality = originalCameraQualityRef.current

              videoTrack.applyConstraints?.(originalQuality).then(() => {
                isCameraQualityReducedRef.current = false
                originalCameraQualityRef.current = null
                console.log('[LiveKit] Restored original camera quality')
              }).catch((err: Error) => {
                console.warn('[LiveKit] Failed to restore camera quality:', err)
              })
            }
          } catch (error) {
            console.warn('[LiveKit] Error restoring camera quality:', error)
          }
        }
      }

      const maxRetries = 3
      let retryCount = 0
      let lastError: Error | null = null

      while (retryCount < maxRetries) {
        try {
          const localParticipant = room.localParticipant
          const screenShareTracks = Array.from(localParticipant.trackPublications.values())
            .filter((pub) => pub.source === Track.Source.ScreenShare || pub.source === Track.Source.ScreenShareAudio)

          // Cleanup existing screen share tracks: when stopping OR when starting (ensures clean state for re-start after takeover)
          if (!enabled || screenShareTracks.length > 0) {
            for (const publication of screenShareTracks) {
              try {
                if (publication.track) {
                  publication.track.stop()
                }
                if (publication.track) {
                  await localParticipant.unpublishTrack(publication.track)
                }
              } catch (cleanupError) {
                console.warn('[LiveKit] Error cleaning up screen share track:', cleanupError)
              }
            }
          }

          await room.localParticipant.setScreenShareEnabled(enabled, SCREEN_SHARE_CAPTURE_OPTIONS)
          if (enabled && screenShareSessionIdRef.current) {
            sendScreenShareSessionEvent(Message.START_SCREEN_SHARE_SESSION, screenShareSessionIdRef.current)
          }
          setScreenShareEnabled(enabled)
          screenShareOperationInFlightRef.current = false
          dispatchConnectionStatus(room.state as ConnectionState, room)
          return // Success, exit retry loop
        } catch (error) {
          lastError = error as Error

          // ── Cancellation guard ──────────────────────────────────────────────
          // When the user dismisses the native screen-share picker the browser
          // throws NotAllowedError or AbortError. Retrying would re-open the
          // picker immediately, creating an infinite loop. Bail out cleanly.
          const isCancellation = isNonRetryableScreenShareError(lastError)

          if (isCancellation) {
            console.log('[LiveKit] Screen share picker cancelled by user — aborting without retry')
            cleanupFailedScreenShareStart()
            screenShareOperationInFlightRef.current = false
            return // Don't retry, don't show error banner
          }
          // ────────────────────────────────────────────────────────────────────

          retryCount++

          if (retryCount < maxRetries) {
            // Wait before retry with exponential backoff
            const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 5000)
            console.warn(`[LiveKit] Screen share operation failed, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})`)
            await new Promise(resolve => setTimeout(resolve, delay))

            // Check connection state before retry
            const currentState = room.state as ConnectionState
            if (currentState === ConnectionState.Reconnecting || currentState === ConnectionState.Disconnected) {
              console.warn('[LiveKit] Connection lost during retry, aborting screen share operation')
              break
            }
          }
        }
      }

      // All retries failed
      console.error('[LiveKit] Error toggling screen share after retries:', lastError)
      // Ensure state is consistent even on error
      cleanupFailedScreenShareStart()
      screenShareOperationInFlightRef.current = false

      // Dispatch error event
      window.dispatchEvent(
        new CustomEvent('livekit-failsafe-error', {
          detail: { message: `Failed to ${enabled ? 'start' : 'stop'} screen share: ${lastError?.message || 'Unknown error'}` },
        })
      )
    },
    [room]
  )

  // Listen for device toggle events
  useEffect(() => {
    const handleAudioToggle = (event: CustomEvent<{ enabled: boolean }>) => {
      toggleAudio(event.detail.enabled)
    }
    const handleVideoToggle = (event: CustomEvent<{ enabled: boolean }>) => {
      const enabled = event.detail.enabled

      // Turning the camera OFF never rebuilds the pipeline — muting a track does
      // not restart its processor, so no WebGL context is allocated. It must also
      // never be blocked or delayed: "stop my camera" is precisely the action a
      // user needs to take effect immediately. It leaves a running cooldown
      // alone: "off" is clickable during it, so ending it here would let on,
      // off, on rebuild the processor with no throttle at all.
      if (!enabled) {
        toggleVideo(false)
        return
      }

      // Turning it ON does rebuild: LiveKit restarts the track, the library
      // destroys and re-inits the processor, and that allocates a fresh WebGL
      // context. Unbounded churn exhausts the page's context budget and evicts
      // the game canvas, which never recovers. The sources of this event refuse
      // an "on" for the same window before they show or save it
      // (useVideoDisplayState.toggleVideo, the call bar's fix actions), and the
      // controls disable their button. This is only the backstop for a caller that
      // does not: it cannot undo what that caller saved, so it must not guess.
      if (readVideoToggleBusy()) {
        console.warn('[LiveKit] SC-158: ignoring camera-on while the background pipeline is rebuilding')
        return
      }
      // Only when a processor will actually be rebuilt. A preference carried to a
      // device that cannot run effects must not lock its camera toggle.
      if (isBackgroundEffectActive(backgroundEffectRef.current)) {
        beginVideoToggleCooldown()
      }
      toggleVideo(true)
    }
    const handleScreenShareToggle = (event: CustomEvent<{ enabled: boolean }>) => {
      toggleScreenShare(event.detail.enabled)
    }
    const handleComputerScreenShareRequest = (
      event: CustomEvent<ComputerScreenShareRequestDetail>
    ) => {
      const resolution = resolveComputerScreenShareRequest(event.detail.computerId, {
        activeComputerId: activeComputerIdRef.current,
        isScreenShareEnabled: screenShareEnabled,
        isScreenShareStarting: screenShareOperationInFlightRef.current,
      })

      activeComputerIdRef.current = resolution.activeComputerId

      if (resolution.shouldStartScreenShare) {
        toggleScreenShare(true)
      }
    }
    const handleNativeComputerScreenShareIntent = (
      event: CustomEvent<NativeComputerScreenShareIntent>
    ) => {
      const resolution = resolveComputerScreenShareRequest(event.detail.computerId, {
        activeComputerId: activeComputerIdRef.current,
        isScreenShareEnabled: screenShareEnabled,
        isScreenShareStarting: screenShareOperationInFlightRef.current,
      })

      activeComputerIdRef.current = resolution.activeComputerId

      if (resolution.shouldStartScreenShare) {
        dispatchComputerScreenShareRequest(event.detail.computerId)
        void toggleScreenShare(true)
      }
    }
    const handleInheritSessionId = (event: CustomEvent<{ sessionId: string }>) => {
      screenShareSessionIdRef.current = event.detail.sessionId
    }
    // SC-158: the picker lives in the GameMenu tree, the processor in this
    // connector; they share no React ancestor, so they meet on a window event —
    // the same bridge the audio/video toggles already use.
    const handleSetBackgroundEffect = (event: CustomEvent<SetBackgroundEffectDetail>) => {
      const effect = event.detail?.effect
      if (!effect) return
      // Saved by the lifecycle once the pick is the selection — not before: a
      // pick that fails while another effect is running is not kept, and the
      // running one stays selected and saved.
      //
      // The pre-join dialog opts out: it saves for itself, because it cannot
      // assume this connector exists yet.
      void applyVideoBackgroundRef.current?.(room, effect, {
        persist: event.detail?.persist !== false,
      })
    }

    window.addEventListener(LIVEKIT_TOGGLE_AUDIO_EVENT, handleAudioToggle as EventListener)
    window.addEventListener(LIVEKIT_TOGGLE_VIDEO_EVENT, handleVideoToggle as EventListener)
    window.addEventListener(
      LIVEKIT_SET_BACKGROUND_EFFECT_EVENT,
      handleSetBackgroundEffect as EventListener
    )
    window.addEventListener(
      LIVEKIT_TOGGLE_SCREEN_SHARE_EVENT,
      handleScreenShareToggle as EventListener
    )
    window.addEventListener(
      LIVEKIT_START_COMPUTER_SCREEN_SHARE_EVENT,
      handleComputerScreenShareRequest as EventListener
    )
    window.addEventListener(
      LIVEKIT_NATIVE_COMPUTER_SCREEN_SHARE_INTENT_EVENT,
      handleNativeComputerScreenShareIntent as EventListener
    )
    window.addEventListener(
      LIVEKIT_SCREEN_SHARE_SESSION_ID_EVENT,
      handleInheritSessionId as EventListener
    )
    return () => {
      window.removeEventListener(LIVEKIT_TOGGLE_AUDIO_EVENT, handleAudioToggle as EventListener)
      window.removeEventListener(LIVEKIT_TOGGLE_VIDEO_EVENT, handleVideoToggle as EventListener)
      window.removeEventListener(
        LIVEKIT_SET_BACKGROUND_EFFECT_EVENT,
        handleSetBackgroundEffect as EventListener
      )
      window.removeEventListener(
        LIVEKIT_TOGGLE_SCREEN_SHARE_EVENT,
        handleScreenShareToggle as EventListener
      )
      window.removeEventListener(
        LIVEKIT_START_COMPUTER_SCREEN_SHARE_EVENT,
        handleComputerScreenShareRequest as EventListener
      )
      window.removeEventListener(
        LIVEKIT_NATIVE_COMPUTER_SCREEN_SHARE_INTENT_EVENT,
        handleNativeComputerScreenShareIntent as EventListener
      )
      window.removeEventListener(
        LIVEKIT_SCREEN_SHARE_SESSION_ID_EVENT,
        handleInheritSessionId as EventListener
      )
    }
  }, [screenShareEnabled, toggleAudio, toggleVideo, toggleScreenShare, beginVideoToggleCooldown])

  // Comprehensive cleanup on component unmount ONLY.
  // IMPORTANT: This effect uses an empty dependency array so it runs cleanup
  // exclusively on unmount — NOT on every room/roomConnected change.
  // All current values are accessed through refs so the cleanup always sees
  // the latest state. The previous implementation had [room, roomConnected, ...]
  // as dependencies, which caused its cleanup to fire every time 'room' or
  // 'roomConnected' changed. That cleanup unconditionally reset the state
  // machine to DISCONNECTED, which:
  //   1) Destroyed the CONNECTING state mid-connection
  //   2) On the next roomConnected change, saw state=DISCONNECTED + room≠null
  //      and called room.disconnect(), immediately killing the just-established
  //      connection.
  useEffect(() => {
    return () => {
      // Clear all timers
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current)
        fallbackTimerRef.current = null
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }

      if (pageLeaveTimeoutRef.current) {
        clearTimeout(pageLeaveTimeoutRef.current)
        pageLeaveTimeoutRef.current = null
      }

      if (adaptiveQualityTimerRef.current) {
        clearTimeout(adaptiveQualityTimerRef.current)
        adaptiveQualityTimerRef.current = null
      }

      // Bug 13: Clear state transition unlock timer on unmount
      if (stateTransitionUnlockTimerRef.current) {
        clearTimeout(stateTransitionUnlockTimerRef.current)
        stateTransitionUnlockTimerRef.current = null
      }

      // Bug 14: Clear device enable timeout on unmount
      if (deviceEnableTimeoutRef.current) {
        clearTimeout(deviceEnableTimeoutRef.current)
        deviceEnableTimeoutRef.current = null
      }

      clearDelayedCameraEnable()
      ++cameraEnableOpIdRef.current
      cameraEnableInProgressRef.current = false

      // Disconnect room if still connected (access via ref for latest value)
      const currentRoom = roomRef.current
      // SC-158 (review #5): retired before the disconnect, like every other
      // disconnect path. The hold preview and the held / problem state live at
      // module level and outlive this instance, so a remount (an error-boundary
      // retry) inherited them, and a hold preview kept the camera capturing.
      backgroundLifecycleRef.current?.retireRoom(currentRoom)
      backgroundLifecycleRef.current?.forgetUser()
      if (currentRoom) {
        try {
          currentRoom.disconnect()
        } catch (error) {
          console.warn('[LiveKit] Error during cleanup disconnect:', error)
        }
      }

      // Destroy denoiser processor
      if (denoiserProcessorRef.current) {
        try {
          denoiserProcessorRef.current.destroy()
        } catch { /* ignore */ }
        denoiserProcessorRef.current = null
      }

      // SC-158: destroy the background processor too — it holds a WebGL context
      // and a render loop that would otherwise outlive the component.
      if (videoProcessorRef.current) {
        try {
          void destroyBackgroundProcessor(videoProcessorRef.current)
        } catch { /* ignore */ }
        videoProcessorRef.current = null
      }
      // A parked processor was never attached to a track, so LiveKit will never
      // stop it — nothing else would ever release its canvas.
      if (parkedCaptureProcessorRef.current) {
        try {
          void destroyBackgroundProcessor(parkedCaptureProcessorRef.current.processor)
        } catch { /* ignore */ }
        parkedCaptureProcessorRef.current = null
      }
      backgroundFrameStatsRef.current = []
      if (videoToggleCooldownTimerRef.current) {
        clearTimeout(videoToggleCooldownTimerRef.current)
        videoToggleCooldownTimerRef.current = null
      }
      // The busy state outlives this component, and with the timer gone
      // nothing else would clear it: the next camera button would mount busy.
      publishVideoToggleBusy(false)

      // Reset state machine
      connectionStateRef.current = LiveKitConnectionStateMachine.DISCONNECTED
      stateTransitionLockRef.current = false
      connectionLockRef.current = false

      // Reset reconnection attempts
      reconnectAttemptsRef.current = 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearDelayedCameraEnable]) // Current values accessed via refs.

  // Dispatch connection status event
  function dispatchConnectionStatus(state: ConnectionState, room?: Room) {
    if (!room) {
      setLivekitAnalyticsSnapshot(null, 0)
      const connectionStatus: LiveKitConnectionStatus = {
        state,
        quality: undefined,
        participants: 0,
        participantsList: [],
        room: undefined,
      }
      window.dispatchEvent(new CustomEvent(LIVEKIT_CONNECTION_EVENT, { detail: connectionStatus }))
      return
    }
    const participantsList = []
    const localP = room.localParticipant
    if (localP) {
      const hasAudio = localP.isMicrophoneEnabled
      const hasVideo = localP.isCameraEnabled
      const hasScreenShare = localP.isScreenShareEnabled
      participantsList.push({
        id: localP.identity,
        name: localP.name || 'Me (local)',
        hasAudio,
        hasVideo,
        hasScreenShare,
      })
      setAudioEnabled(hasAudio)
      setVideoEnabled(hasVideo)
      setScreenShareEnabled(hasScreenShare)
    }
    room.remoteParticipants.forEach((participant) => {
      const hasAudio = Array.from(participant.trackPublications.values()).some(
        (pub) => pub.kind === 'audio' && !pub.isMuted
      )
      const hasVideo = Array.from(participant.trackPublications.values()).some(
        (pub) => pub.kind === 'video' && !pub.isMuted && pub.source === Track.Source.Camera
      )
      const hasScreenShare = Array.from(participant.trackPublications.values()).some(
        (pub) => pub.kind === 'video' && !pub.isMuted && pub.source === Track.Source.ScreenShare
      )
      participantsList.push({
        id: participant.identity,
        name: participant.name || participant.identity,
        hasAudio,
        hasVideo,
        hasScreenShare,
      })
    })
    const colyId = roomDataRef.current?.colyseusRoomId ?? null
    if (state === ConnectionState.Connected) {
      setLivekitAnalyticsSnapshot(colyId, room.numParticipants || 0)
    } else {
      setLivekitAnalyticsSnapshot(null, 0)
    }
    const connectionStatus: LiveKitConnectionStatus = {
      state,
      quality: room.localParticipant?.connectionQuality,
      participants: room.numParticipants || 0,
      participantsList,
      room,
    }
    window.dispatchEvent(new CustomEvent(LIVEKIT_CONNECTION_EVENT, { detail: connectionStatus }))
  }

  // Only render when we have room and roomData
  if (!roomData || !roomData.token || !roomData.serverUrl || !room) {
    return null
  }

  return (
    <RoomContext.Provider value={room}>
      {roomConnected ? (
        <LiveKitErrorBoundary>
          <LiveKitVideoDisplay />
        </LiveKitErrorBoundary>
      ) : null}
      {roomConnected && audioPlaybackBlocked ? (
        <LiveKitAudioResumePrompt
          onResume={() => { void tryResumeAudioPlayback(roomRef.current, 'user-gesture') }}
        />
      ) : null}
    </RoomContext.Provider>
  )
}

export default LiveKitRoomConnector
